import '../scss/components/_ownerrevenue.scss'
import React, { useState, useEffect } from 'react'
import RentReceipt from './rentReceipt' // TODO: adjust import path to wherever rentReceipt.js actually lives
import {
  getPGRevenueStatus,
  autoGenerateRent,
  process_rent_payment,
  autoGenerateElectricityBill,
  getTenantStatement,
  getSecurityDeposit,
  getTenantPendingExpenses,
  receiveSecurityDeposit,
  adjustSecurityDeposit,
  refundSecurityDeposit,
  receiveTenantExpense,
  getSettlement,
  generateSettlement,
  processSettlement,
  getOverallDue,
  processOverallPayment,
  sendWhatsAppOverdueReminder,
  getReceiptJsonData,
  getReceiptJsonDataSingle,
  getTenantAdvance
} from '../apis/services/pgApi'

// ─── AnimatedHeight ─────────────────────────────────────────────
// Wraps content that changes size (tab switches, async loads, etc.)
// and smoothly animates the container height instead of letting the
// UI jump/collapse. Mirrors the kind of soft, physical resize you get
// in native (Apple-style) UIs.
const AnimatedHeight = ({ children, className = '', duration = 320 }) => {
  const innerRef = React.useRef(null)
  const [height, setHeight] = useState('auto')
  const [isAnimating, setIsAnimating] = useState(false)
  const firstRun = React.useRef(true)

  useEffect(() => {
    const node = innerRef.current
    if (!node) return

    const applyHeight = () => {
      const next = node.getBoundingClientRect().height
      setHeight(prev => {
        if (firstRun.current) {
          firstRun.current = false
          return next
        }
        if (Math.abs((typeof prev === 'number' ? prev : next) - next) > 1) {
          setIsAnimating(true)
          window.clearTimeout(applyHeight._t)
          applyHeight._t = window.setTimeout(() => setIsAnimating(false), duration + 40)
        }
        return next
      })
    }

    applyHeight()

    const ro = new ResizeObserver(() => applyHeight())
    ro.observe(node)
    return () => ro.disconnect()
  }, [children, duration])

  return (
    <div
      className={`rv-animated-height ${isAnimating ? 'rv-animated-height-active' : ''} ${className}`}
      style={{ height: typeof height === 'number' ? `${height}px` : height, transitionDuration: `${duration}ms` }}
    >
      <div ref={innerRef} className="rv-animated-height-inner">
        {children}
      </div>
    </div>
  )
}

// ─── Skeleton placeholders (used while async content is loading, so
// panels keep roughly the right shape instead of collapsing to a spinner) ──
const SkeletonLine = ({ w = '100%', h = 12 }) => (
  <span className="rv-skel-line" style={{ width: w, height: h }} />
)

const SettlementSkeleton = () => (
  <div className="rv-settlement-skeleton">
    <div className="rv-settlement-wrapper">
      <div className="rv-settlement-left">
        {[1, 2, 3, 4].map(i => (
          <div className="rv-settlement-item" key={i}>
            <SkeletonLine w="45%" />
            <SkeletonLine w="25%" />
          </div>
        ))}
      </div>
      <div className="rv-settlement-right">
        {[1, 2].map(i => (
          <div className="rv-settlement-item" key={i}>
            <SkeletonLine w="55%" />
            <SkeletonLine w="30%" />
          </div>
        ))}
      </div>
    </div>
    <div className="rv-settlement-payment-fields">
      <div className="rv-settlement-field-row">
        {[1, 2, 3, 4].map(i => (
          <div className="rv-settlement-field-group" key={i}>
            <SkeletonLine w="60%" h={10} />
            <span className="rv-skel-line rv-skel-input" />
          </div>
        ))}
      </div>
    </div>
    <span className="rv-skel-line rv-skel-btn" />
  </div>
)

const STATUS_CFG = {
  PAID: { label: 'Paid', dot: 'green', bg: '#F0FDF4', border: 'green', text: 'green' },
  DUE: { label: 'Due', dot: 'blue', bg: '#f0f6ff', border: 'blue', text: 'blue' },
  OVERDUE: { label: 'Overdue', dot: 'orange', bg: '#FFF7ED', border: 'orange', text: 'orange' },
  "NOT PAID": { label: 'Not Paid', dot: 'red', bg: '#FFF1F2', border: 'red', text: 'red' },
  VACANT: { label: 'Vacant', dot: 'gray', bg: '#F9FAFB', border: 'gray', text: 'gray' },
}

const RENT_STATUS_CFG = {
  PAID: { label: 'Paid', dot: 'green', bg: '#F0FDF4', border: 'green', text: 'green' },
  DUE: { label: 'Due', dot: 'blue', bg: '#f0f6ff', border: 'blue', text: 'blue' },
  OVERDUE: { label: 'Overdue', dot: 'orange', bg: '#FFF7ED', border: 'orange', text: 'orange' },
  "NOT PAID": { label: 'Not Paid', dot: 'red', bg: '#FFF1F2', border: 'red', text: 'red' },
  NA: { label: 'N/A', dot: 'gray', bg: '#F9FAFB', border: 'gray', text: 'gray' },
}

const fmt = n => `₹${(n || 0).toLocaleString('en-IN')}`

const STATUS_FILTERS = [
  { key: 'PAID', label: 'Paid', bg: 'green' },
  { key: 'DUE', label: 'Due', bg: 'blue' },
  { key: 'OVERDUE', label: 'Overdue', bg: 'orange' },
  { key: 'NOT PAID', label: 'Not Paid', bg: 'red' },
]

const getStatusColor = (status) => {
  switch (status) {
    case 'PAID':
      return { bg: '#DCFCE7', color: '#166534', dot: '#22C55E' }
    case 'DUE':
      return { bg: '#f0f6ff', color: '#1b2d80', dot: '#1e49c0' }
    case 'OVERDUE':
      return { bg: '#FFEDD5', color: '#9A3412', dot: '#F97316' }
    case 'NOT PAID':
      return { bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444' }
    default:
      return { bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF' }
  }
}

const SecurityDepositModal = ({ depositData, admissionId, onClose, onTransactionComplete, embedded = false }) => {
  const [transactionType, setTransactionType] = useState('RECEIVED') // radio default
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('') // NEW: optional description
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('success')
  const [transactions, setTransactions] = useState([])
  const [depositInfo, setDepositInfo] = useState(null)

  // Additional fields for ADJUSTED
  const [referenceType, setReferenceType] = useState('TENANTEXPENSE')
  const [referenceId, setReferenceId] = useState('9')
  const [adjustPaymentMode, setAdjustPaymentMode] = useState('CASH')
  const [adjustReferenceNo, setAdjustReferenceNo] = useState('')

  // Load deposit data with transactions
  useEffect(() => {
    if (depositData) {
      setDepositInfo(depositData)
      setTransactions(depositData.Transactions || [])
    }
  }, [depositData])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!transactionType || !amount || parseFloat(amount) <= 0) {
      setMessage('Please select transaction type and enter a valid amount')
      setMessageType('error')
      return
    }

    setIsSubmitting(true)
    setMessage('')

    try {
      const parsedAmount = parseFloat(amount)
      const userId = parseInt(localStorage.getItem('userId') || 1)
      let response

      switch (transactionType) {
        case 'RECEIVED':
          response = await receiveSecurityDeposit({
            AdmissionID: admissionId,
            Amount: parsedAmount,
            Remarks: description || 'Security Deposit Received',
            CreatedBy: userId
          })
          break

        case 'ADJUSTED':
          if (!depositInfo?.DepositID) {
            throw new Error('Deposit ID not found')
          }
          response = await adjustSecurityDeposit({
            DepositID: depositInfo.DepositID,
            ReferenceType: referenceType || 'TENANTEXPENSE',
            ReferenceID: parseInt(referenceId) || 9,
            Amount: parsedAmount,
            PaymentMode: adjustPaymentMode || 'CASH',
            ReferenceNo: adjustReferenceNo || null,
            Remarks: description || `Security deposit adjustment - ${referenceType}`,
            CreatedBy: userId
          })
          break

        default:
          throw new Error('Invalid transaction type')
      }

      // Check if response indicates success
      if (response && response.success === false) {
        throw new Error(response.message || 'Transaction failed')
      }

      setMessage('Transaction completed successfully!')
      setMessageType('success')

      // Refresh deposit data
      const updatedDeposit = await getSecurityDeposit(admissionId)
      if (updatedDeposit?.data) {
        setDepositInfo(updatedDeposit.data)
        setTransactions(updatedDeposit.data.Transactions || [])
        if (onTransactionComplete) {
          onTransactionComplete(updatedDeposit.data)
        }
      }

      // Reset form
      setTransactionType('RECEIVED')
      setAmount('')
      setDescription('')
      setReferenceType('TENANTEXPENSE')
      setReferenceId('9')
      setAdjustPaymentMode('CASH')
      setAdjustReferenceNo('')

      // Auto close after 2 seconds
      setTimeout(() => {
        onClose()
      }, 2000)

    } catch (error) {
      console.error('Transaction error:', error)
      setMessage(error.message || 'Transaction failed. Please try again.')
      setMessageType('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setTransactionType('RECEIVED')
    setAmount('')
    setDescription('')
    setReferenceType('TENANTEXPENSE')
    setReferenceId('9')
    setAdjustPaymentMode('CASH')
    setAdjustReferenceNo('')
    setMessage('')
    setTimeout(() => {
      onClose()
    }, 200)
  }

  if (!depositInfo) {
    const emptyContent = (
      <div className={embedded ? 'rv-sd-embedded' : 'rv-sd-modal'} onClick={e => e.stopPropagation()}>
        {!embedded && (
          <div className="rv-sd-head">
            <div>
              <h3 className="rv-sd-title">Security Deposit Overview</h3>
              <p className="rv-sd-subtitle">Summary of security deposit transactions</p>
            </div>
            <button className="rv-sd-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}
        <div className="rv-sd-body">
          <div className="rv-sd-loading">No deposit details found</div>
        </div>
      </div>
    )
    if (embedded) return emptyContent
    return (
      <div className="rv-modal-overlay" onClick={onClose}>
        {emptyContent}
      </div>
    )
  }

  const getStatusColorClass = (transactionType) => {
    switch (transactionType) {
      case 'RECEIVED': return 'rv-sd-type-received'
      case 'ADJUSTED': return 'rv-sd-type-adjusted'
      case 'REFUND': return 'rv-sd-type-refund'
      default: return ''
    }
  }

  const currentRemaining = (depositInfo.ReceivedAmount || 0) - (depositInfo.AdjustedAmount || 0)

  // Fallback: if the API doesn't send BalanceAfter per transaction, derive it
  // by walking backward from the current balance. Assumes `transactions` is
  // ordered newest-first (as returned by the API / shown in the table); if
  // it's actually oldest-first, we detect that from the dates and reverse.
  const computeFallbackBalances = (txs, currentBalance) => {
    if (!txs || txs.length === 0) return []
    const first = new Date(txs[0].TransactionDate).getTime()
    const last = new Date(txs[txs.length - 1].TransactionDate).getTime()
    const isNewestFirst = first >= last
    const orderedNewestFirst = isNewestFirst ? txs : [...txs].reverse()

    let running = currentBalance
    const balances = orderedNewestFirst.map((tx) => {
      const balanceAfterThis = running
      const amt = parseFloat(tx.Amount) || 0
      running = tx.TransactionType === 'RECEIVED' ? running - amt : running + amt
      return balanceAfterThis
    })

    return isNewestFirst ? balances : balances.reverse()
  }

  const fallbackBalances = computeFallbackBalances(
    transactions,
    depositInfo.Balance ?? currentRemaining
  )

  const sdContent = (
    <div className={embedded ? 'rv-sd-embedded' : 'rv-sd-modal'} onClick={e => e.stopPropagation()}>
      {!embedded && (
        <div className="rv-sd-head">
          <div>
            <h3 className="rv-sd-title">Security Deposit Overview</h3>
            <p className="rv-sd-subtitle">Summary of security deposit transactions</p>
          </div>
          <button className="rv-sd-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      <div className="rv-sd-body">
        {/* Stat Cards — 4 cards (Current Balance card removed) */}
        <div className="rv-sd-stats-grid">
          <div className="rv-sd-stat-card">
            <div className="rv-sd-stat-icon rv-sd-stat-icon-blue">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="7" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <span className="rv-sd-stat-label">Total Security Deposit</span>
            <span className="rv-sd-stat-sublabel">(All Received)</span>
            <span className="rv-sd-stat-value rv-sd-stat-value-blue">{fmt(depositInfo.DepositAmount)}</span>
          </div>

          <div className="rv-sd-stat-card">
            <div className="rv-sd-stat-icon rv-sd-stat-icon-green">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 4v16M12 20l-6-6M12 20l6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="rv-sd-stat-label">Total Received</span>
            <span className="rv-sd-stat-sublabel">(All Time)</span>
            <span className="rv-sd-stat-value rv-sd-stat-value-green">{fmt(depositInfo.ReceivedAmount)}</span>
          </div>

          <div className="rv-sd-stat-card">
            <div className="rv-sd-stat-icon rv-sd-stat-icon-orange">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 20V4M12 4l-6 6M12 4l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="rv-sd-stat-label">Total Adjusted</span>
            <span className="rv-sd-stat-sublabel">(All Time)</span>
            <span className="rv-sd-stat-value rv-sd-stat-value-orange">{fmt(depositInfo.AdjustedAmount)}</span>
          </div>

          <div className="rv-sd-stat-card">
            <div className="rv-sd-stat-icon rv-sd-stat-icon-purple">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <span className="rv-sd-stat-label">Current Remaining</span>
            <span className="rv-sd-stat-sublabel">(Refundable)</span>
            <span className="rv-sd-stat-value rv-sd-stat-value-purple">{fmt(depositInfo.Balance ?? currentRemaining)}</span>
          </div>
        </div>

        {/* Info Banner */}
        <div className="rv-sd-banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Current Remaining = Total Received − Total Adjusted</span>
        </div>

        {/* Transaction History */}
        <div className="rv-sd-section">
          <h4 className="rv-sd-section-title">Transaction History</h4>
          <div className="rv-sd-table-wrapper">
            {transactions && transactions.length > 0 ? (
              <table className="rv-sd-table">
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Balance After</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, index) => (
                    <tr key={tx.TransactionID || index}>
                      <td>{new Date(tx.TransactionDate).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</td>
                      <td>
                        <span className={`rv-sd-transaction-type ${getStatusColorClass(tx.TransactionType)}`}>
                          <span className={`rv-sd-type-icon ${tx.TransactionType === 'RECEIVED' ? 'rv-sd-type-icon-green' : 'rv-sd-type-icon-orange'}`}>
                            {tx.TransactionType === 'RECEIVED' ? (
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                                <path d="M12 4v16M12 20l-6-6M12 20l6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                                <path d="M12 20V4M12 4l-6 6M12 4l6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          {tx.TransactionType === 'RECEIVED' ? 'Received' : 'Adjusted'}
                        </span>
                      </td>
                      <td className={tx.TransactionType === 'RECEIVED' ? 'rv-sd-amount-positive' : 'rv-sd-amount-negative'}>
                        {tx.TransactionType === 'RECEIVED' ? '+ ' : '- '}{fmt(tx.Amount)}
                      </td>
                      <td className="rv-sd-balance-after">
                        {fmt(tx.BalanceAfter != null ? tx.BalanceAfter : fallbackBalances[index])}
                      </td>
                      <td className="rv-sd-description">{tx.Remarks || tx.Description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="rv-sd-empty">No transactions found</div>
            )}
          </div>
        </div>

        {/* Add Transaction Form */}
        <div className="rv-sd-section rv-sd-action-section">
          <h4 className="rv-sd-section-title">Add Transaction</h4>

          <form onSubmit={handleSubmit}>
            <div className="rv-sd-form-group">
              <label className="rv-sd-form-label">Type</label>
              <div className="rv-sd-radio-group">
                <label className={`rv-sd-radio-option ${transactionType === 'RECEIVED' ? 'rv-sd-radio-option-active-green' : ''}`}>
                  <input
                    type="radio"
                    name="transactionType"
                    value="RECEIVED"
                    checked={transactionType === 'RECEIVED'}
                    onChange={(e) => setTransactionType(e.target.value)}
                  />
                  <span className="rv-sd-radio-dot rv-sd-radio-dot-green"></span>
                  Receive Security Deposit
                </label>
                <label className={`rv-sd-radio-option ${transactionType === 'ADJUSTED' ? 'rv-sd-radio-option-active-orange' : ''}`}>
                  <input
                    type="radio"
                    name="transactionType"
                    value="ADJUSTED"
                    checked={transactionType === 'ADJUSTED'}
                    onChange={(e) => setTransactionType(e.target.value)}
                  />
                  <span className="rv-sd-radio-dot rv-sd-radio-dot-orange"></span>
                  Adjust Security Deposit
                </label>
              </div>
            </div>

            <div className="rv-sd-form-row" style={{ marginTop: '12px' }}>
              <div className="rv-sd-form-group">
                <label className="rv-sd-form-label">Amount (₹)</label>
                <input
                  type="number"
                  className="rv-sd-form-input"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              <div className="rv-sd-form-group">
                <label className="rv-sd-form-label">Description (Optional)</label>
                <input
                  type="text"
                  className="rv-sd-form-input"
                  placeholder="Enter description or reason"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            {/* Additional fields for ADJUSTED */}
            {transactionType === 'ADJUSTED' && (
              <>
                <div className="rv-sd-form-row" style={{ marginTop: '12px' }}>
                  <div className="rv-sd-form-group">
                    <label className="rv-sd-form-label">Payment Mode</label>
                    <select
                      className="rv-sd-form-select"
                      value={adjustPaymentMode}
                      onChange={(e) => setAdjustPaymentMode(e.target.value)}
                    >
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="BANK">Bank Transfer</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </div>
                  <div className="rv-sd-form-group">
                    <label className="rv-sd-form-label">Reference No</label>
                    <input
                      type="text"
                      className="rv-sd-form-input"
                      placeholder="Enter reference number"
                      value={adjustReferenceNo}
                      onChange={(e) => setAdjustReferenceNo(e.target.value)}
                    />
                  </div>
                </div>

                <div className="rv-sd-form-row" style={{ marginTop: '12px' }}>
                  <div className="rv-sd-form-group">
                    <label className="rv-sd-form-label">Adjustment Head</label>
                    <select
                      className="rv-sd-form-select"
                      value={referenceType}
                      onChange={(e) => {
                        setReferenceType(e.target.value)
                        if (e.target.value === 'TENANTEXPENSE') {
                          setReferenceId('9')
                        } else if (e.target.value === 'RENTINVOICE') {
                          setReferenceId('3')
                        } else if (e.target.value === 'ELECTRICITY') {
                          setReferenceId('6')
                        } else if (e.target.value === 'FOOD') {
                          setReferenceId('7')
                        }
                      }}
                      required
                    >
                      <option value="TENANTEXPENSE">Tenant Expense</option>
                      <option value="RENTINVOICE">Rent Invoice</option>
                      <option value="ELECTRICITY">Electricity</option>
                      <option value="FOOD">Food</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {message && (
              <div className={`rv-sd-message rv-sd-message-${messageType}`}>
                {message}
              </div>
            )}

            <div className="rv-sd-note">
              <strong>Note:</strong> Total Security Deposit is the cumulative amount of all deposits received from the tenant. Current Remaining is the refundable amount available.
            </div>

            <div className="rv-sd-form-actions">
              <button
                type="button"
                className="rv-sd-btn rv-sd-btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rv-sd-btn rv-sd-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="rv-sd-spinner"></span>
                    Processing...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )

  if (embedded) return sdContent

  return (
    <div className="rv-modal-overlay" onClick={onClose}>
      {sdContent}
    </div>
  )
}


// ─── Pending Expenses Modal ────────────────────────────────
const PendingExpensesModal = ({ expenses, admissionId, pgId, onClose, onSuccess }) => {
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMode, setPaymentMode] = useState('CASH')
  const [narration, setNarration] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('success')
  const [referenceNo, setReferenceNo] = useState('')

  // Calculate total pending amount
  const totalPendingAmount = expenses.reduce((sum, expense) => sum + (expense.NetPayable || 0), 0)

  // Validate payment amount
  const isPaymentValid = () => {
    if (!paymentAmount) return false
    const amount = parseFloat(paymentAmount)
    return amount > 0 && amount <= totalPendingAmount
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const amount = parseFloat(paymentAmount)

    if (!paymentAmount || amount <= 0) {
      setMessage('Please enter a valid payment amount')
      setMessageType('error')
      return
    }

    if (amount > totalPendingAmount) {
      setMessage(`Payment amount cannot exceed total pending amount of ${fmt(totalPendingAmount)}`)
      setMessageType('error')
      return
    }

    if (!admissionId) {
      setMessage('Admission ID not found')
      setMessageType('error')
      return
    }

    setIsProcessing(true)
    setMessage('')

    try {
      const response = await receiveTenantExpense({
        AdmissionID: admissionId,
        Amount: amount,
        PaymentMode: paymentMode,
        Narration: narration || `Payment against pending expenses`,
        ReferenceNo: referenceNo.trim() || '',
        UserID: parseInt(localStorage.getItem('userId')) || 1,
      })

      if (response && response.success === false) {
        throw new Error(response.message || 'Payment failed')
      }

      setMessage(`Payment of ${fmt(amount)} received successfully!`)
      setMessageType('success')

      // Reset form
      setPaymentAmount('')
      setNarration('')
      setReferenceNo('')

      // Notify parent to refresh data
      if (onSuccess) {
        onSuccess()
      }

      // Auto close after 2 seconds
      setTimeout(() => {
        onClose()
      }, 2000)

    } catch (error) {
      console.error('Payment error:', error)
      setMessage(error.message || 'Payment failed. Please try again.')
      setMessageType('error')
    } finally {
      setIsProcessing(false)
    }
  }

  const resetForm = () => {
    setPaymentAmount('')
    setNarration('')
    setReferenceNo('')
    setMessage('')
    setTimeout(() => {
      onClose()
    }, 200)
  }

  if (!expenses || expenses.length === 0) {
    return (
      <div className="rv-modal-overlay" onClick={onClose}>
        <div className="rv-pe-modal" onClick={e => e.stopPropagation()}>
          <div className="rv-pe-head">
            <h3 className="rv-pe-title">Pending Expenses</h3>
            <button className="rv-pe-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="rv-pe-body">
            <p className="rv-pe-empty">No pending expenses found</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rv-modal-overlay" onClick={onClose}>
      <div className="rv-pe-modal" onClick={e => e.stopPropagation()}>
        <div className="rv-pe-head">
          <h3 className="rv-pe-title">Pending Expenses</h3>
          <button className="rv-pe-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="rv-pe-body">
          {/* Expense List - No Checkboxes */}
          <div className="rv-pe-table-wrapper">
            <table className="rv-pe-table">
              <thead>
                <tr>
                  {/* <th>Expense ID</th> */}
                  <th>Expense</th>
                  <th>Expense Date</th>
                  <th>Amount</th>
                  <th>Adjusted Amount</th>
                  <th>Paid Amount</th>
                  <th>Net Payable</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.ExpenseID}>
                    {/* <td>{expense.ExpenseID}</td> */}
                    <td>{expense.LedgerName}</td>
                    <td>{new Date(expense.ExpenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td className="rv-pe-amount">{fmt(expense.Amount)}</td>
                    <td className="rv-pe-amount" style={{ fontWeight: '700' }}>
                      {fmt(expense.AdvanceAdjusted || 0)}
                    </td>
                    <td className="rv-pe-amount" style={{ fontWeight: '700' }}>
                      {fmt(expense.PaidAmount || 0)}
                    </td>
                    <td className="rv-pe-amount" style={{ fontWeight: '700' }}>
                      {fmt(expense.NetPayable || 0)}
                    </td>
                    <td>{expense.Description || '-'}</td>
                    <td>{expense.Status || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total Pending Amount */}
          <div className="rv-pe-total">
            <span>Total Pending Amount:</span>
            <strong style={{ color: '#2563EB' }}>{fmt(totalPendingAmount)}</strong>
          </div>

          {/* Payment Form */}
          {/* <div className="rv-pe-payment-section">
            <h4 className="rv-pe-section-title">Make Payment</h4>
            
            <form onSubmit={handleSubmit}>
              <div className="rv-pe-form-row">
                <div className="rv-pe-form-group">
                  <label className="rv-pe-form-label">Payment Amount</label>
                  <div className="rv-pe-amount-input-wrap">
                    <span className="rv-pe-currency-symbol">₹</span>
                    <input
                      type="number"
                      className={`rv-pe-form-input ${paymentAmount && parseFloat(paymentAmount) > totalPendingAmount ? 'rv-pe-form-input-error' : ''}`}
                      placeholder="Enter amount"
                      value={paymentAmount}
                      onChange={(e) => {
                        const value = e.target.value
                        setPaymentAmount(value)
                        // Clear message when user types
                        if (message) setMessage('')
                      }}
                      step="0.01"
                      min="0.01"
                      max={totalPendingAmount}
                      required
                    />
                  </div>
                  {paymentAmount && parseFloat(paymentAmount) > totalPendingAmount && (
                    <span className="rv-pe-form-hint rv-pe-form-hint-error">
                      Amount exceeds total pending amount
                    </span>
                  )}
                  {paymentAmount && parseFloat(paymentAmount) <= totalPendingAmount && parseFloat(paymentAmount) > 0 && (
                    <span className="rv-pe-form-hint rv-pe-form-hint-success">
                      ✓ Valid amount
                    </span>
                  )}
                </div>

                <div className="rv-pe-form-group">
                  <label className="rv-pe-form-label">Payment Mode</label>
                  <select
                    className="rv-pe-form-select"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    required
                  >
                    <option value="CASH">💵 Cash</option>
                    <option value="UPI">📱 UPI</option>
                    <option value="BANK">🏦 Bank Transfer</option>
                    <option value="CHEQUE">📝 Cheque</option>
                  </select>
                </div>
              </div>

              <div className="rv-pe-form-row">
                <div className="rv-pe-form-group" style={{ marginTop: '12px' }}>
                  <label className="rv-pe-form-label">Reference No</label>
                  <input
                    type="text"
                    className="rv-pe-form-input"
                    placeholder="Enter Reference number"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                  />
                </div>

                <div className="rv-pe-form-group" style={{ marginTop: '12px' }}>
                  <label className="rv-pe-form-label">Narration</label>
                  <input
                    type="text"
                    className="rv-pe-form-input"
                    placeholder="Enter narration"
                    value={narration}
                    onChange={(e) => setNarration(e.target.value)}
                  />
                </div>
              </div>


              {message && (
                <div className={`rv-pe-message rv-pe-message-${messageType}`}>
                  {message}
                </div>
              )}

              <div className="rv-pe-form-actions">
                <button
                  type="button"
                  className="rv-pe-btn rv-pe-btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rv-pe-btn rv-pe-btn-primary"
                  disabled={isProcessing || !isPaymentValid()}
                >
                  {isProcessing ? (
                    <>
                      <span className="rv-pe-spinner"></span>
                      Processing...
                    </>
                  ) : (
                    `Pay ${paymentAmount ? fmt(parseFloat(paymentAmount)) : ''}`
                  )}
                </button>
              </div>
            </form>
          </div> */}
        </div>
      </div>
    </div>
  )
}

const formatCurrency = (val) => {
  const n = Number(val) || 0;
  return `₹${n.toLocaleString('en-IN')}`;
};

// ─── Receipt fetching (get-receipt-json) ───────────────────────
// TODO: once OwnerRevenue starts receiving the receiptNo from wherever it's
// loaded (props/route/context), swap this out. For now it's hardcoded so the
// Pay flow can be wired end-to-end.
const FIXED_RECEIPT_NO = 'RCPT-2026-000175';
const FIXED_RECEIPT_ID = 10;

const RECEIPT_API_URL = 'https://ciplpgapi.azurewebsites.net/receipt/get-receipt-json';

// The API wraps the real payload as a JSON-stringified value under this
// SQL Server "FOR JSON AUTO" style key — needs a second JSON.parse.
const RECEIPT_JSON_FIELD = 'JSON_F52E2B61-18A1-11d1-B105-00805F49916B';

// const fetchReceiptByNo = async (receiptNo) => {
//   const res = await fetch(`${RECEIPT_API_URL}?receiptNo=${encodeURIComponent(receiptNo)}`);
//   if (!res.ok) {
//     throw new Error(`Receipt request failed (${res.status})`);
//   }

//   const json = await res.json();
//   if (!json?.success || !json?.data) {
//     throw new Error(json?.message || 'Receipt fetch failed');
//   }

//   const rawField = json.data[RECEIPT_JSON_FIELD];
//   if (!rawField) {
//     throw new Error('Receipt response missing expected data field');
//   }

//   const parsed = JSON.parse(rawField);
//   // Payload comes back as an array containing a single object:
//   // [{ Header, PaymentSummary, Rent, Electricity, Pending }]
//   return Array.isArray(parsed) ? parsed[0] : parsed;
// };


// const fetchReceiptByNo = async (receiptNo) => {
//   try {
//     // Call the existing getReceiptJsonData function
//     const response = await getReceiptJsonData(receiptNo);

//     // Check if response is successful
//     if (!response?.success || !response?.data) {
//       throw new Error(response?.message || 'Receipt fetch failed');
//     }

//     const rawField = response.data[RECEIPT_JSON_FIELD];
//     if (!rawField) {
//       throw new Error('Receipt response missing expected data field');
//     }

//     // Parse the JSON string from the SQL Server FOR JSON field
//     const parsed = JSON.parse(rawField);

//     // Payload comes back as an array containing a single object:
//     // [{ Header, PaymentSummary, Rent, Electricity, Pending }]
//     return Array.isArray(parsed) ? parsed[0] : parsed;

//   } catch (error) {
//     console.error('Error fetching receipt:', error);
//     throw new Error(error.message || 'Failed to fetch receipt');
//   }
// };

const fetchReceiptByNo = async (ReceiptID) => {
  try {
    // Call the existing getReceiptJsonData function
    const response = await getReceiptJsonDataSingle(ReceiptID);

    // Check if response is successful
    if (!response?.success || !response?.data) {
      throw new Error(response?.message || 'Receipt fetch failed');
    }

    const rawField = response.data[RECEIPT_JSON_FIELD];
    if (!rawField) {
      throw new Error('Receipt response missing expected data field');
    }

    // Parse the JSON string from the SQL Server FOR JSON field
    const parsed = JSON.parse(rawField);

    // Payload comes back as an array containing a single object:
    // [{ Header, PaymentSummary, Rent, Electricity, Pending }]
    return Array.isArray(parsed) ? parsed[0] : parsed;

  } catch (error) {
    console.error('Error fetching receipt:', error);
    throw new Error(error.message || 'Failed to fetch receipt');
  }
};


const formatReceiptDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(String(dateStr).replace(' ', 'T'));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatRentPeriod = (rentEntries) => {
  const entry = rentEntries?.[0];
  if (!entry?.InvoiceMonth || !entry?.InvoiceYear) return '';
  const start = new Date(entry.InvoiceYear, entry.InvoiceMonth - 1, 1);
  const end = new Date(entry.InvoiceYear, entry.InvoiceMonth, 0); // last day of month
  const startLabel = start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const endLabel = end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
};

const PAYMENT_MODE_LABELS = {
  CASH: 'Cash',
  ONLINE: 'Online Transfer',
  UPI: 'UPI',
  CARD: 'Card Payment',
  BANKTRANSFER: 'Bank Transfer',
  CHEQUE: 'Cheque',
};


// const mapReceiptToRentReceiptProps = (parsed, advanceData = null) => {
//   const header = parsed?.Header?.[0] || {};
//   const rentEntries = parsed?.Rent || [];
//   const paymentSummary = parsed?.PaymentSummary || [];
//   const rentPeriod = formatRentPeriod(rentEntries);

//   // Calculate advance balance if advance data is provided
//   let advanceBalance = 0;
//   if (advanceData && advanceData.advanceDeposit && advanceData.advanceDeposit.length > 0) {
//     advanceBalance = advanceData.advanceDeposit[0].Balance || 0;
//   }

//   // Determine receipt type based on PaymentSummary
//   let receiptType = 'RENT';

//   // Count how many payment types have amount > 0
//   const paidTypes = paymentSummary.filter(item => (item.Amount || 0) > 0);

//   if (paidTypes.length > 1) {
//     receiptType = 'PAYMENT';
//   } else if (paidTypes.length === 1) {
//     const type = paidTypes[0].ReferenceType;
//     if (type === 'RENT') receiptType = 'RENT';
//     else if (type === 'Electricity' || type === 'ELECTRICITY') receiptType = 'ELECTRICITY';
//     else if (type === 'EXPENSE') receiptType = 'EXPENSE';
//     else if (type === 'ADVANCE') receiptType = 'ADVANCE';
//     else receiptType = 'PAYMENT';
//   }

//   // Get payment breakdown details
//   const rentPayment = paymentSummary.find(item => item.ReferenceType === 'RENT') || {};
//   const electricityPayment = paymentSummary.find(item => item.ReferenceType === 'ELECTRICITY' || item.ReferenceType === 'Electricity') || {};
//   const expensePayment = paymentSummary.find(item => item.ReferenceType === 'EXPENSE') || {};
//   const advancePayment = paymentSummary.find(item => item.ReferenceType === 'ADVANCE') || {};

//   return {
//     pgName: header.PGName || '',
//     paidBy: (header.TenantName || '').trim(),
//     amount: Math.round(Number(header.TotalPaid) || 0).toLocaleString('en-IN'),
//     paidOn: formatReceiptDate(header.ReceiptDate),
//     period: rentPeriod || formatReceiptDate(header.ReceiptDate),
//     periodLabel: rentPeriod ? 'Rent Period' : 'Payment Date',
//     paymentMethod: PAYMENT_MODE_LABELS[header.PaymentMode] || header.PaymentMode || '',
//     receiptNo: header.ReferenceNo || '',
//     receiptNumber: header.ReceiptNo || '',
//     receiptType: receiptType,
//     // Additional fields for advance
//     advanceBalance: advanceBalance,
//     rentAmount: rentPayment.Amount || 0,
//     electricityAmount: electricityPayment.Amount || 0,
//     expenseAmount: expensePayment.Amount || 0,
//     advanceAmount: advancePayment.Amount || 0,
//     totalPaid: header.TotalPaid || 0,
//   };
// };

const mapReceiptToRentReceiptProps = (parsed) => {
  const header = parsed?.Header?.[0] || {};
  const rentEntries = parsed?.Rent || [];
  const paymentSummary = parsed?.PaymentSummary || [];
  const rentPeriod = formatRentPeriod(rentEntries);

  // Determine receipt type dynamically - NO hardcoded types!
  let receiptType = 'RENT';
  
  // Count how many payment types have amount > 0
  const paidTypes = paymentSummary.filter(item => (item.Amount || 0) > 0);
  
  if (paidTypes.length > 1) {
    // Multiple payment types = combined payment receipt
    receiptType = 'PAYMENT';
  } else if (paidTypes.length === 1) {
    // Single payment type = use that type name (whatever it is)
    // This will work for RENT, ELECTRICITY, EXPENSE, ADVANCE, SECURITY, MAINTENANCE, etc.
    receiptType = paidTypes[0].ReferenceType;
  }
  // If no payment types with amount, default to RENT

  return {
    pgName: header.PGName || '',
    paidBy: (header.TenantName || '').trim(),
    amount: Math.round(Number(header.TotalPaid) || 0).toLocaleString('en-IN'),
    paidOn: formatReceiptDate(header.ReceiptDate),
    period: rentPeriod || formatReceiptDate(header.ReceiptDate),
    periodLabel: rentPeriod ? 'Rent Period' : 'Payment Date',
    paymentMethod: PAYMENT_MODE_LABELS[header.PaymentMode] || header.PaymentMode || '',
    receiptNo: header.ReferenceNo || '',
    receiptNumber: header.ReceiptNo || '',
    receiptType: receiptType, // Dynamic: 'PAYMENT' or any single type like 'SECURITY'
    roomNumber: header.RoomNumber || '',
    bedNumber: header.BedNumber || '',
    paymentSummary: paymentSummary || [], // All payment types passed through
  };
};

const IconHouse = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 11.5L12 4L21 11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 10V20H19V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.5 20V14H14.5V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconBolt = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M13 2L4 14H11L10 22L20 9H13L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const IconDoc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M6 3H15L19 7V21H6V3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M9 12H16M9 16H16M9 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconClose = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ─── Full and Final Settlement (used as an inline tab in OverallRentModal) ────────
const FullFinalSettlementSection = ({ admissionId, onSettled }) => {
  const [settlementData, setSettlementData] = useState(null)
  const [isLoadingSettlement, setIsLoadingSettlement] = useState(false)
  const [isGeneratingSettlement, setIsGeneratingSettlement] = useState(false)
  const [isProcessingSettlement, setIsProcessingSettlement] = useState(false)
  const [settlementError, setSettlementError] = useState('')
  const [settlementSuccess, setSettlementSuccess] = useState('')

  const [settleAmount, setSettleAmount] = useState('')
  const [amountReceived, setAmountReceived] = useState('')
  const [amountRefunded, setAmountRefunded] = useState('')
  const [checkoutDate, setCheckoutDate] = useState('')

  const [settlementValidationError, setSettlementValidationError] = useState('')

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const parseNumber = (val) => {
    const num = parseFloat(val)
    return isNaN(num) ? 0 : num
  }

  const formatSettlementAmount = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`

  const validateSettlementFields = () => {
    const netPayable = parseNumber(settlementData?.NetPayable)
    const settle = parseNumber(settleAmount)
    const received = parseNumber(amountReceived)
    const refund = parseNumber(amountRefunded)

    if (netPayable === 0) {
      if (settle !== 0 || received !== 0 || refund !== 0) {
        setSettlementValidationError('When Net Payable is 0, all settlement amounts must be 0')
        return false
      }
      setSettlementValidationError('')
      return true
    }

    if (netPayable > 0) {
      if (settle === 0 && received === 0) {
        setSettlementValidationError('Please enter either Settle Amount or Amount Received')
        return false
      }
      if (settle + received !== netPayable) {
        setSettlementValidationError(
          `Settle Amount (₹${settle}) + Amount Received (₹${received}) must equal Net Payable (₹${netPayable})`
        )
        return false
      }
      if (received > netPayable) {
        setSettlementValidationError(`Amount Received (₹${received}) cannot exceed Net Payable (₹${netPayable})`)
        return false
      }
      if (settle > netPayable) {
        setSettlementValidationError(`Settle Amount (₹${settle}) cannot exceed Net Payable (₹${netPayable})`)
        return false
      }
      if (refund > 0) {
        setSettlementValidationError('Amount Refunded should be 0 when Net Payable is positive')
        return false
      }
      setSettlementValidationError('')
      return true
    }

    if (netPayable < 0) {
      const absNetPayable = Math.abs(netPayable)
      if (settle > 0 || received > 0) {
        setSettlementValidationError('Settle Amount and Amount Received should be 0 when Net Payable is negative')
        return false
      }
      if (refund !== absNetPayable) {
        setSettlementValidationError(
          `Amount Refunded (₹${refund}) must equal ${absNetPayable} (absolute value of Net Payable)`
        )
        return false
      }
      if (refund === 0) {
        setSettlementValidationError(`Amount Refunded must be ₹${absNetPayable} to settle the negative balance`)
        return false
      }
      setSettlementValidationError('')
      return true
    }

    return true
  }

  const isSettlementSubmitDisabled = () => {
    if (isProcessingSettlement) return true
    if (!settlementData) return true
    if (settlementData.Status === 'CLOSED' || settlementData.Status === 'COMPLETED') return true

    const netPayable = parseNumber(settlementData?.NetPayable)

    const hasSettleAmount = settleAmount !== '' && settleAmount !== null
    const hasAmountReceived = amountReceived !== '' && amountReceived !== null
    const hasAmountRefunded = amountRefunded !== '' && amountRefunded !== null

    if (netPayable > 0) {
      if (!hasSettleAmount && !hasAmountReceived) return true
      const settle = parseNumber(settleAmount)
      const received = parseNumber(amountReceived)
      if (hasSettleAmount && isNaN(settle)) return true
      if (hasAmountReceived && isNaN(received)) return true
      if (settle + received !== netPayable) return true
      if (settle > netPayable) return true
      if (received > netPayable) return true
      return false
    }

    if (netPayable < 0) {
      const absNetPayable = Math.abs(netPayable)
      if (hasSettleAmount && parseNumber(settleAmount) !== 0) return true
      if (hasAmountReceived && parseNumber(amountReceived) !== 0) return true
      if (!hasAmountRefunded) return true
      if (parseNumber(amountRefunded) !== absNetPayable) return true
      return false
    }

    if (netPayable === 0) {
      if (hasSettleAmount && parseNumber(settleAmount) !== 0) return true
      if (hasAmountReceived && parseNumber(amountReceived) !== 0) return true
      if (hasAmountRefunded && parseNumber(amountRefunded) !== 0) return true
      return false
    }

    return true
  }

  const fetchSettlement = async (id) => {
    if (!id) return

    setIsLoadingSettlement(true)
    setSettlementError('')
    setSettlementSuccess('')
    setSettlementValidationError('')

    try {
      const response = await getSettlement(id)
      if (response?.success && response?.data) {
        setSettlementData(response.data)

        if (response.data.CheckoutDate) {
          const date = new Date(response.data.CheckoutDate)
          const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000))
          const year = istDate.getFullYear()
          const month = String(istDate.getMonth() + 1).padStart(2, '0')
          const day = String(istDate.getDate()).padStart(2, '0')
          setCheckoutDate(`${year}-${month}-${day}`)
        }
      } else {
        setSettlementData(null)
      }
    } catch (err) {
      console.error('Error fetching settlement:', err)
      setSettlementData(null)
    } finally {
      setIsLoadingSettlement(false)
    }
  }

  const handleGenerateSettlement = async () => {
    if (!admissionId) {
      setSettlementError('No admission ID found')
      return
    }

    setIsGeneratingSettlement(true)
    setSettlementError('')
    setSettlementSuccess('')
    setSettlementValidationError('')

    try {
      const requestData = {
        AdmissionID: admissionId,
        CheckoutDate: new Date().toISOString().split('T')[0],
        Remarks: 'Final settlement generation',
        CreatedBy: parseInt(localStorage.getItem('userId')) || 1
      }

      const response = await generateSettlement(requestData)

      if (response?.success && response?.data) {
        setSettlementSuccess('Settlement generated successfully!')
        await fetchSettlement(admissionId)
      } else {
        setSettlementError(response?.message || 'Failed to generate settlement')
      }
    } catch (err) {
      console.error('Error generating settlement:', err)
      setSettlementError(err.message || 'Failed to generate settlement')
    } finally {
      setIsGeneratingSettlement(false)
    }
  }

  const handleProcessSettlement = async () => {
    if (!settlementData?.SettlementID) {
      setSettlementError('No settlement ID found')
      return
    }

    if (!validateSettlementFields()) return

    const received = parseNumber(amountReceived)
    const refund = parseNumber(amountRefunded)
    const settle = parseNumber(settleAmount)

    setIsProcessingSettlement(true)
    setSettlementError('')
    setSettlementSuccess('')
    setSettlementValidationError('')

    try {
      const userId = parseInt(localStorage.getItem('userId')) || 1

      const requestData = {
        SettlementID: settlementData.SettlementID,
        AmountReceived: received,
        AmountRefunded: refund,
        AmountSettlement: settle,
        CheckoutDate: checkoutDate || null,
        CreatedBy: userId
      }

      const response = await processSettlement(requestData)

      if (response?.success === true) {
        const successMsg = response?.message || 'Settlement processed successfully!'
        setSettlementSuccess(successMsg)
        alert(successMsg)
        await fetchSettlement(admissionId)
        if (onSettled) onSettled()
      } else {
        setSettlementError(response?.message || 'Failed to process settlement')
      }
    } catch (err) {
      console.error('Error processing settlement:', err)
      setSettlementError(err.message || 'Failed to process settlement')
    } finally {
      setIsProcessingSettlement(false)
    }
  }

  useEffect(() => {
    if (settlementData) {
      const netPayable = parseNumber(settlementData.NetPayable)
      if (netPayable > 0) {
        setSettleAmount('')
        setAmountReceived('')
        setAmountRefunded('0')
      } else if (netPayable < 0) {
        setSettleAmount('0')
        setAmountReceived('0')
        setAmountRefunded(Math.abs(netPayable).toString())
      } else {
        setSettleAmount('0')
        setAmountReceived('0')
        setAmountRefunded('0')
      }
    }
  }, [settlementData])

  useEffect(() => {
    if (admissionId) {
      fetchSettlement(admissionId)
    }
  }, [admissionId])

  const getValidationMessage = () => {
    console.log("settlementData", settlementData);
    
    const netPayable = parseNumber(settlementData?.NetPayable)

    if (netPayable > 0) {
      const settle = parseNumber(settleAmount)
      const received = parseNumber(amountReceived)
      const sum = settle + received

      if (settleAmount === '' && amountReceived === '') {
        return { type: 'info', message: `Enter either Settle Amount or Amount Received to total ₹${netPayable}` }
      }
      if (settleAmount !== '' && amountReceived !== '') {
        if (sum !== netPayable) {
          return {
            type: 'error',
            message: `Settle Amount (₹${settle}) + Amount Received (₹${received}) = ₹${sum}, but Net Payable is ₹${netPayable}`
          }
        }
        return { type: 'success', message: `✓ Total matches Net Payable (₹${netPayable})` }
      }
      if (settleAmount !== '') {
        if (settle === netPayable) {
          return { type: 'success', message: `✓ Settle Amount matches Net Payable (₹${netPayable})` }
        }
        return { type: 'error', message: `Settle Amount (₹${settle}) must equal Net Payable (₹${netPayable})` }
      }
      if (amountReceived !== '') {
        if (received === netPayable) {
          return { type: 'success', message: `✓ Amount Received matches Net Payable (₹${netPayable})` }
        }
        return { type: 'error', message: `Amount Received (₹${received}) must equal Net Payable (₹${netPayable})` }
      }
    }

    if (netPayable < 0) {
      const absNetPayable = Math.abs(netPayable)
      const refund = parseNumber(amountRefunded)

      if (amountRefunded === '') {
        return { type: 'info', message: `Enter Amount Refunded of ₹${absNetPayable}` }
      }
      if (refund !== absNetPayable) {
        return { type: 'error', message: `Amount Refunded (₹${refund}) must equal ₹${absNetPayable}` }
      }
      return { type: 'success', message: `✓ Amount Refunded matches (₹${absNetPayable})` }
    }

    if (netPayable === 0) {
      return { type: 'success', message: '✓ Net Payable is 0, no settlement amount required' }
    }

    return null
  }

  return (
    <div className="rv-settlement-section rv-settlement-section-embedded">
      {isLoadingSettlement && !settlementData ? (
        <SettlementSkeleton />
      ) : settlementData ? (
        <>
          <div className="rv-settlement-wrapper">
            <div className="rv-settlement-left">
              <div className="rv-settlement-item">
                <span className="rv-settlement-label">Rent Due</span>
                <span className="rv-settlement-value rv-settlement-amount">{formatSettlementAmount(settlementData.RentDue)}</span>
              </div>
              <div className="rv-settlement-item">
                <span className="rv-settlement-label">Electricity Due</span>
                <span className="rv-settlement-value rv-settlement-amount">{formatSettlementAmount(settlementData.ElectricityDue)}</span>
              </div>
              <div className="rv-settlement-item">
                <span className="rv-settlement-label">Expense Due</span>
                <span className="rv-settlement-value rv-settlement-amount">{formatSettlementAmount(settlementData.ExpenseDue)}</span>
              </div>
              <div className="rv-settlement-item">
                <span className="rv-settlement-label">Total Due</span>
                <span className="rv-settlement-value">{formatSettlementAmount(settlementData.TotalDue)}</span>
              </div>
            </div>

            <div className="rv-settlement-right">
              <div className="rv-settlement-item">
                <span className="rv-settlement-label">Security Deposit</span>
                <span className="rv-settlement-value rv-settlement-deposit">{formatSettlementAmount(settlementData.SecurityDeposit)}</span>
              </div>
              <div className="rv-settlement-item">
                <span className="rv-settlement-label">Net Payable</span>
                <span className="rv-settlement-value rv-settlement-net">{formatSettlementAmount(settlementData.NetPayable)}</span>
              </div>
            </div>
          </div>

          <div className="rv-settlement-payment-fields">
            <div className="rv-settlement-field-row">
              <div className="rv-settlement-field-group" style={{ width: '100%' }}>
                <label className="rv-settlement-field-label">Checkout Date</label>
                <div className="rv-settlement-input-wrap">
                  <input
                    type="date"
                    className="rv-settlement-field-input"
                    value={checkoutDate}
                    onChange={(e) => setCheckoutDate(e.target.value)}
                  />
                </div>
                {settlementData?.CheckoutDate && (
                  <span className="rv-settlement-field-hint">
                    Current: {formatDate(settlementData.CheckoutDate)}
                  </span>
                )}
              </div>

              <div className="rv-settlement-field-group">
                <label className="rv-settlement-field-label">
                  Settle Amount
                  {parseNumber(settlementData?.NetPayable) > 0 && <span className="rv-required">*</span>}
                </label>
                <div className="rv-settlement-input-wrap">
                  <span className="rv-settlement-currency-symbol">₹</span>
                  <input
                    type="number"
                    className={`rv-settlement-field-input ${settlementValidationError && settleAmount !== '' ? 'rv-settlement-field-error' : ''}`}
                    placeholder="Enter settle amount"
                    value={settleAmount}
                    onChange={(e) => {
                      setSettleAmount(e.target.value)
                      setSettlementValidationError('')
                    }}
                    step="0.01"
                    min="0"
                    disabled={parseNumber(settlementData?.NetPayable) <= 0}
                  />
                </div>
                {parseNumber(settlementData?.NetPayable) > 0 && (
                  <span className="rv-settlement-field-hint">Amount to waive/write off</span>
                )}
                {parseNumber(settlementData?.NetPayable) <= 0 && (
                  <span className="rv-settlement-field-hint">
                    Not applicable (Net Payable is {formatSettlementAmount(settlementData?.NetPayable)})
                  </span>
                )}
              </div>

              <div className="rv-settlement-field-group">
                <label className="rv-settlement-field-label">
                  Amount Received
                  {parseNumber(settlementData?.NetPayable) > 0 && <span className="rv-required">*</span>}
                </label>
                <div className="rv-settlement-input-wrap">
                  <span className="rv-settlement-currency-symbol">₹</span>
                  <input
                    type="number"
                    className={`rv-settlement-field-input ${settlementValidationError && amountReceived !== '' ? 'rv-settlement-field-error' : ''}`}
                    placeholder="Enter amount received"
                    value={amountReceived}
                    onChange={(e) => {
                      setAmountReceived(e.target.value)
                      setSettlementValidationError('')
                    }}
                    step="0.01"
                    min="0"
                    disabled={parseNumber(settlementData?.NetPayable) <= 0}
                  />
                </div>
                {parseNumber(settlementData?.NetPayable) > 0 && (
                  <span className="rv-settlement-field-hint">Actual amount paid by tenant</span>
                )}
                {parseNumber(settlementData?.NetPayable) <= 0 && (
                  <span className="rv-settlement-field-hint">
                    Not applicable (Net Payable is {formatSettlementAmount(settlementData?.NetPayable)})
                  </span>
                )}
              </div>

              <div className="rv-settlement-field-group">
                <label className="rv-settlement-field-label">
                  Amount Refunded
                  {parseNumber(settlementData?.NetPayable) < 0 && <span className="rv-required">*</span>}
                </label>
                <div className="rv-settlement-input-wrap">
                  <span className="rv-settlement-currency-symbol">₹</span>
                  <input
                    type="number"
                    className={`rv-settlement-field-input ${settlementValidationError && amountRefunded !== '' ? 'rv-settlement-field-error' : ''}`}
                    placeholder="Enter amount refunded"
                    value={amountRefunded}
                    onChange={(e) => {
                      setAmountRefunded(e.target.value)
                      setSettlementValidationError('')
                    }}
                    step="0.01"
                    min="0"
                    disabled={parseNumber(settlementData?.NetPayable) >= 0}
                  />
                </div>
                {parseNumber(settlementData?.NetPayable) < 0 && (
                  <span className="rv-settlement-field-hint">
                    Must equal {formatSettlementAmount(Math.abs(settlementData.NetPayable))}
                  </span>
                )}
                {parseNumber(settlementData?.NetPayable) >= 0 && (
                  <span className="rv-settlement-field-hint">
                    Not applicable (Net Payable is {formatSettlementAmount(settlementData?.NetPayable)})
                  </span>
                )}
              </div>
            </div>

            {(() => {
              const msg = getValidationMessage()
              if (!msg) return null
              return (
                <div className={`rv-settlement-validation-message ${msg.type === 'success' ? 'rv-settlement-validation-success' :
                  msg.type === 'error' ? 'rv-settlement-validation-error' :
                    'rv-settlement-validation-info'
                  }`}>
                  {msg.message}
                </div>
              )
            })()}
          </div>

          {settlementData.Status !== 'CLOSED' && settlementData.Status !== 'COMPLETED' && (
            <button
              className="rv-settlement-submit-btn"
              onClick={handleProcessSettlement}
              disabled={isSettlementSubmitDisabled()}
            >
              {isProcessingSettlement ? (
                <>
                  <span className="rv-spinner-small"></span>
                  Processing...
                </>
              ) : (
                'Submit Settlement'
              )}
            </button>
          )}

          {settlementData.Status === 'CLOSED' && (
            <div className="rv-settlement-closed-msg">✓ Settlement already processed</div>
          )}
        </>
      ) : (
        <div className="rv-settlement-empty">
          <p className="rv-settlement-empty-text">No settlement found for this tenant</p>
          <button
            className="rv-settlement-generate-btn"
            onClick={handleGenerateSettlement}
            disabled={isGeneratingSettlement}
          >
            {isGeneratingSettlement ? (
              <>
                <span className="rv-spinner-small"></span>
                Generating...
              </>
            ) : (
              'Generate Settlement'
            )}
          </button>
        </div>
      )}

      {settlementError && <div className="rv-settlement-error">{settlementError}</div>}
      {settlementSuccess && <div className="rv-settlement-success">{settlementSuccess}</div>}
    </div>
  )
}

// ─── OverallRentModal (now the primary modal opened from an rv-pill) ─────
// const OverallRentModal = ({
//   pgId,
//   bed,
//   pgName,
//   onClose,
//   onSuccess,
//   onSendReminder
// }) => {
//   const [activeTab, setActiveTab] = useState('overall') // 'overall' | 'security' | 'settlement'
//   const [expandedSection, setExpandedSection] = useState(null) // 'rent' | 'electricity' | 'expense' | null

//   const [formData, setFormData] = useState({
//     RentAmount: '',
//     ElectricityAmount: '',
//     ExpenseAmount: '',
//     PaymentMode: 'CASH',
//     ReferenceNo: '',
//     Narration: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [message, setMessage] = useState('');
//   const [messageType, setMessageType] = useState('success');
//   const [errors, setErrors] = useState({});

//   const [overallDueData, setOverallDueData] = useState(null);
//   const [isLoadingOverallDue, setIsLoadingOverallDue] = useState(false);

//   const [tenantStatement, setTenantStatement] = useState([]);
//   const [isLoadingStatement, setIsLoadingStatement] = useState(false);

//   const [securityDeposit, setSecurityDeposit] = useState(null);
//   const [isLoadingDeposit, setIsLoadingDeposit] = useState(false);

//   const [pendingExpenses, setPendingExpenses] = useState([]);
//   const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
//   const [currentAdmissionId, setCurrentAdmissionId] = useState(null);

//   const [isSendingReminder, setIsSendingReminder] = useState(false);

//   const [receiptData, setReceiptData] = useState(null);
//   const [showReceipt, setShowReceipt] = useState(false);
//   const [isFetchingReceipt, setIsFetchingReceipt] = useState(false);

//   const statusColors = getStatusColor(bed?.rentStatus);
//   const showReminder = ['DUE', 'OVERDUE', 'NOT PAID'].includes(bed?.rentStatus);

//   // ─── Fetchers ────────────────────────────────────────────────
//   const fetchOverallDue = async () => {
//     if (!bed?.admissionId) return;
//     setIsLoadingOverallDue(true);
//     try {
//       const response = await getOverallDue(bed.admissionId);
//       if (response?.success && response?.data) {
//         setOverallDueData(response.data);
//       }
//     } catch (error) {
//       console.error('Error fetching overall due:', error);
//     } finally {
//       setIsLoadingOverallDue(false);
//     }
//   };

//   const fetchTenantStatement = async () => {
//     if (!bed?.admissionId || !pgId) return;
//     setIsLoadingStatement(true);
//     try {
//       const response = await getTenantStatement(bed.admissionId, pgId);
//       setTenantStatement(response || []);
//     } catch (err) {
//       console.error('Error fetching tenant statement:', err);
//       setTenantStatement([]);
//     } finally {
//       setIsLoadingStatement(false);
//     }
//   };

//   const fetchSecurityDeposit = async () => {
//     if (!bed?.admissionId) return;
//     setIsLoadingDeposit(true);
//     try {
//       const response = await getSecurityDeposit(bed.admissionId);
//       setSecurityDeposit(response?.data || null);
//     } catch (err) {
//       console.error('Error fetching security deposit:', err);
//       setSecurityDeposit(null);
//     } finally {
//       setIsLoadingDeposit(false);
//     }
//   };

//   const fetchPendingExpenses = async () => {
//     if (!bed?.admissionId || !pgId) return;
//     setCurrentAdmissionId(bed.admissionId);
//     setIsLoadingExpenses(true);
//     try {
//       const response = await getTenantPendingExpenses(pgId, bed.admissionId);
//       if (response?.data && Array.isArray(response.data)) {
//         setPendingExpenses(response.data);
//       } else {
//         setPendingExpenses([]);
//       }
//     } catch (err) {
//       console.error('Error fetching pending expenses:', err);
//       setPendingExpenses([]);
//     } finally {
//       setIsLoadingExpenses(false);
//     }
//   };

//   useEffect(() => {
//     if (bed?.admissionId) {
//       fetchOverallDue();
//       fetchSecurityDeposit();
//     }
//   }, [bed?.admissionId]);

//   useEffect(() => {
//     if (bed?.admissionId && pgId) {
//       fetchTenantStatement();
//       fetchPendingExpenses();
//     }
//   }, [bed?.admissionId, pgId]);

//   const handleTransactionComplete = (updatedDeposit) => {
//     setSecurityDeposit(updatedDeposit);
//     if (onSuccess) {
//       onSuccess(bed.bedId, updatedDeposit?.Status);
//     }
//   };

//   // ─── WhatsApp reminder ───────────────────────────────────────
//   const sendWhatsAppReminder = async (bedData) => {
//     if (!bedData) return;
//     setIsSendingReminder(true);

//     try {
//       const customerName = bedData.name || 'Tenant';
//       const dueDate = bedData.DueDate || new Date().toISOString();
//       const billingMonth = new Date(dueDate).toLocaleString('en-US', { month: 'long', year: 'numeric' });
//       const roomNumber = bedData.RoomNumber || bedData.roomNumber || 'N/A';
//       const outstandingAmount = bedData.NetPayable || bedData.rent || 0;
//       const pgNameValue = pgName || 'PG';
//       const phoneNumbers = [`91${bedData.PhoneNumber}` || "916260216707"];

//       const payload = {
//         integrated_number: "918796203089",
//         content_type: "template",
//         payload: {
//           messaging_product: "whatsapp",
//           type: "template",
//           template: {
//             name: "aapkapg_overdue_final",
//             language: { code: "en", policy: "deterministic" },
//             namespace: "cbf3b2ba_dca4_40bd_9e8c_747d92ec2d3c",
//             to_and_components: [
//               {
//                 to: phoneNumbers,
//                 components: {
//                   body_customer_name: { type: "text", value: customerName, parameter_name: "customer_name" },
//                   body_billing_month: { type: "text", value: billingMonth, parameter_name: "billing_month" },
//                   body_room_number: { type: "text", value: roomNumber.toString(), parameter_name: "room_number" },
//                   body_outstanding_amount: { type: "text", value: outstandingAmount.toString(), parameter_name: "outstanding_amount" },
//                   body_due_date: {
//                     type: "text",
//                     value: new Date(dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
//                     parameter_name: "due_date"
//                   },
//                   body_pg_name: { type: "text", value: pgNameValue, parameter_name: "pg_name" }
//                 }
//               }
//             ]
//           }
//         }
//       };

//       const response = await sendWhatsAppOverdueReminder(payload);

//       if (response?.success) {
//         if (onSendReminder) onSendReminder(bedData);
//         alert(`✅ WhatsApp reminder sent to ${customerName}!`);
//       } else {
//         alert(`❌ Failed to send reminder: ${response?.message || 'Unknown error'}`);
//       }
//     } catch (error) {
//       console.error('Error sending WhatsApp reminder:', error);
//       const errorMessage = error.response?.data?.message || error.message || 'Failed to send WhatsApp reminder';
//       alert(`❌ Error: ${errorMessage}`);
//     } finally {
//       setIsSendingReminder(false);
//     }
//   };

//   const handleReminderClick = () => {
//     if (bed) {
//       sendWhatsAppReminder(bed);
//     }
//   };

//   // ─── Overall payment submit ──────────────────────────────────
//   const validate = () => {
//     const newErrors = {};
//     const { RentAmount, ElectricityAmount, ExpenseAmount, PaymentMode } = formData;

//     const rent = parseFloat(RentAmount) || 0;
//     const electricity = parseFloat(ElectricityAmount) || 0;
//     const expense = parseFloat(ExpenseAmount) || 0;

//     if (rent <= 0 && electricity <= 0 && expense <= 0) {
//       newErrors.general = 'At least one amount must be greater than 0';
//     }
//     if (!PaymentMode) {
//       newErrors.PaymentMode = 'Please select payment mode';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validate()) return;

//     setIsSubmitting(true);
//     setMessage('');

//     try {
//       const userId = localStorage.getItem('userId') || 1;

//       const payload = {
//         AdmissionID: bed?.admissionId,
//         RentAmount: parseFloat(formData.RentAmount) || 0,
//         ElectricityAmount: parseFloat(formData.ElectricityAmount) || 0,
//         ExpenseAmount: parseFloat(formData.ExpenseAmount) || 0,
//         PaymentMode: formData.PaymentMode,
//         DueinMonth: 0,
//         ReferenceNo: formData.ReferenceNo?.trim() || '',
//         UserId: parseInt(userId)
//       };

//       const response = await processOverallPayment(payload);
//       console.log("response", response)
//       if (response?.success) {
//         setMessage('Payment processed successfully!');
//         setMessageType('success');

//         fetchOverallDue();
//         fetchTenantStatement();
//         fetchPendingExpenses();

//         if (onSuccess) {
//           onSuccess(bed.bedId, '');
//         }

//         // Fixed receiptNo for now — once the backend returns it on the payment
//         // response (or OwnerRevenue is handed it on load), swap FIXED_RECEIPT_NO
//         // for that value.
//       // const receiptNoToFetch = response?.data?.Receipt?.ReceiptNo || FIXED_RECEIPT_NO;
//       const receiptIdToFetch = response?.data?.Receipt?.ReceiptID || FIXED_RECEIPT_ID;
//       const receiptNoToFetch = FIXED_RECEIPT_NO;

//         try {
//           setIsFetchingReceipt(true);
//       // const parsedReceipt = await fetchReceiptByNo(receiptNoToFetch);
//       const parsedReceipt = await fetchReceiptByNo(receiptIdToFetch);
//       setReceiptData(mapReceiptToRentReceiptProps(parsedReceipt));
//       setShowReceipt(true);
//         } catch (receiptErr) {
//           console.error('Error fetching receipt:', receiptErr);
//           // Receipt fetch failing shouldn't block the payment success flow —
//           // just fall back to closing the modal like before.
//           setTimeout(() => onClose(), 1500);
//         } finally {
//           setIsFetchingReceipt(false);
//         }
//       } else {
//         throw new Error(response?.message || 'Payment failed');
//       }
//     } catch (error) {
//       console.error('Error processing overall payment:', error);
//       setMessage(error.message || 'Payment failed. Please try again.');
//       setMessageType('error');
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleChange = (field, value) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//     if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
//     if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
//   };

//   const rentDue = overallDueData?.RentNetPayable || 0;
//   const electricityDue = overallDueData?.ElectricityNetPayable || 0;
//   const expenseDue = overallDueData?.ExpenseNetPayable || 0;
//   const totalDue = overallDueData?.TotalDue ?? (rentDue + electricityDue + expenseDue);

//   const totalPayable =
//     (parseFloat(formData.RentAmount) || 0) +
//     (parseFloat(formData.ElectricityAmount) || 0) +
//     (parseFloat(formData.ExpenseAmount) || 0);

//   const toggleSection = (section) => {
//     setExpandedSection(prev => (prev === section ? null : section));
//   };

//   if (!bed) return null;

//   return (
//     <div className="rp-overlay" onClick={onClose}>
//       <div className="rp-modal" onClick={e => e.stopPropagation()}>
//         <div className="rp-header">
//           <div>
//             <h3 className="rp-title">{bed.name || 'Tenant'}</h3>
//             <p className="rp-subtitle">
//               <span className="rv-rp-badge" style={{ background: statusColors.bg, color: statusColors.color }}>
//                 <span className="rv-rp-dot" style={{ background: statusColors.dot }} />
//                 {bed.rentStatus === 'NOT PAID' ? 'NOT PAID' : (bed.rentStatus || 'NA')}
//               </span>
//             </p>
//           </div>
//           <div className="rp-header-actions">
//             {showReminder && (
//               <button
//                 className="rv-rp-reminder-btn"
//                 onClick={handleReminderClick}
//                 disabled={isSendingReminder}
//                 title="Send WhatsApp reminder"
//               >
//                 {isSendingReminder ? (
//                   <>
//                     <span className="rv-spinner-small"></span>
//                     Sending...
//                   </>
//                 ) : (
//                   <>
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                       <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
//                       <path d="M13.73 21a2 2 0 01-3.46 0" />
//                     </svg>
//                     Send Reminder
//                   </>
//                 )}
//               </button>
//             )}
//             <button className="rp-close" onClick={onClose} aria-label="Close">
//               <IconClose />
//             </button>
//           </div>
//         </div>

//         {/* ─── Tabs ─────────────────────────────────────────── */}
//         <div className="rp-tabs">
//           <button
//             className={`rp-tab ${activeTab === 'overall' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('overall')}
//           >
//             Regular Payment
//           </button>
//           <button
//             className={`rp-tab ${activeTab === 'security' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('security')}
//           >
//             Security Deposit
//           </button>
//           <button
//             className={`rp-tab ${activeTab === 'settlement' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('settlement')}
//           >
//             Full and Final Payment
//           </button>
//         </div>

//         <div className="rp-body">
//           <AnimatedHeight>
//             {activeTab === 'overall' && (
//               <>
//                 <div className="rp-table">
//                   <div className="rp-table-row rp-table-head">
//                     <div className="rp-col rp-col-label"></div>
//                     <div className="rp-col rp-col-due">Due Summary</div>
//                     <div className="rp-col rp-col-pay">Payment Details</div>
//                   </div>

//                   {/* Rent Row */}
//                   <div className="rp-table-row rp-history-row">
//                     <div
//                       className="rp-col rp-col-label rp-col-clickable"
//                       onClick={() => toggleSection('rent')}
//                     >
//                       <span className="rp-icon rp-icon-rent"><IconHouse /></span>
//                       <span className="rp-item-name">Rent</span>
//                       <span className={`rp-history-caret ${expandedSection === 'rent' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Due</span>
//                       <span className="rp-due-value">{formatCurrency(rentDue)}</span>
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Pay</span>
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.RentAmount}
//                           onChange={(e) => handleChange('RentAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                   {expandedSection === 'rent' && (
//                     <div className="rp-history-panel">
//                       {isLoadingStatement ? (
//                         <div className="rv-statement-loading">
//                           <span className="rv-spinner-small"></span> Loading rent history...
//                         </div>
//                       ) : tenantStatement.length > 0 ? (
//                         <div className="rv-statement-table-wrapper">
//                           <table className="rv-statement-table">
//                             <thead>
//                               <tr>
//                                 <th>Month</th>
//                                 <th>Year</th>
//                                 <th>Monthly Rent (₹)</th>
//                                 <th>Rent Due (₹)</th>
//                                 <th>Paid (₹)</th>
//                                 <th>Security Adjusted (₹)</th>
//                                 <th>Net Payable (₹)</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {tenantStatement.map((item, idx) => (
//                                 <tr key={idx}>
//                                   <td>{item.MonthName}</td>
//                                   <td>{item.InvoiceYear}</td>
//                                   <td>{(item.RentAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td>{(item.TotalAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-paid-amount">{(item.PaidAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-adjusted-amount">{(item.AdvanceAdjusted || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-netpayable-amount">{(item.NetPayable || 0).toLocaleString('en-IN')}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <div className="rv-pe-empty">No rent history found</div>
//                       )}
//                     </div>
//                   )}

//                   {/* Electricity Row */}
//                   <div className="rp-table-row rp-history-row">
//                     <div
//                       className="rp-col rp-col-label rp-col-clickable"
//                       onClick={() => toggleSection('electricity')}
//                     >
//                       <span className="rp-icon rp-icon-electricity"><IconBolt /></span>
//                       <span className="rp-item-name">Electricity</span>
//                       <span className={`rp-history-caret ${expandedSection === 'electricity' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Due</span>
//                       <span className="rp-due-value">{formatCurrency(electricityDue)}</span>
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Pay</span>
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.ElectricityAmount}
//                           onChange={(e) => handleChange('ElectricityAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                   {expandedSection === 'electricity' && (
//                     <div className="rp-history-panel">
//                       {/* Static for now — electricity history API not yet integrated */}
//                       <div className="rv-statement-table-wrapper">
//                         <table className="rv-statement-table">
//                           <thead>
//                             <tr>
//                               <th>Month</th>
//                               <th>Year</th>
//                               <th>Monthly Electricity (₹)</th>
//                               <th>Payment Due (₹)</th>
//                               <th>Paid (₹)</th>
//                               <th>Security Adjusted (₹)</th>
//                               <th>Net Payable (₹)</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {(tenantStatement.length > 0 ? tenantStatement : [{}]).map((item, idx) => (
//                               <tr key={idx}>
//                                 <td>{item.MonthName || '-'}</td>
//                                 <td>{item.InvoiceYear || '-'}</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   )}

//                   {/* Other Expenses Row */}
//                   <div className="rp-table-row rp-history-row">
//                     <div
//                       className="rp-col rp-col-label rp-col-clickable"
//                       onClick={() => toggleSection('expense')}
//                     >
//                       <span className="rp-icon rp-icon-expense"><IconDoc /></span>
//                       <span className="rp-item-name">Other Expenses</span>
//                       <span className={`rp-history-caret ${expandedSection === 'expense' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Due</span>
//                       <span className="rp-due-value">{formatCurrency(expenseDue)}</span>
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Pay</span>
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.ExpenseAmount}
//                           onChange={(e) => handleChange('ExpenseAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                   {expandedSection === 'expense' && (
//                     <div className="rp-history-panel">
//                       {isLoadingExpenses ? (
//                         <div className="rv-statement-loading">
//                           <span className="rv-spinner-small"></span> Loading expenses...
//                         </div>
//                       ) : pendingExpenses.length > 0 ? (
//                         <div className="rv-pe-table-wrapper">
//                           <table className="rv-pe-table">
//                             <thead>
//                               <tr>
//                                 <th>Expense</th>
//                                 <th>Expense Date</th>
//                                 <th>Amount</th>
//                                 <th>Adjusted Amount</th>
//                                 <th>Paid Amount</th>
//                                 <th>Net Payable</th>
//                                 <th>Description</th>
//                                 <th>Status</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {pendingExpenses.map((expense) => (
//                                 <tr key={expense.ExpenseID}>
//                                   <td>{expense.LedgerName}</td>
//                                   <td>{new Date(expense.ExpenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
//                                   <td className="rv-pe-amount">{fmt(expense.Amount)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.AdvanceAdjusted || 0)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.PaidAmount || 0)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.NetPayable || 0)}</td>
//                                   <td>{expense.Description || '-'}</td>
//                                   <td>{expense.Status || '-'}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <div className="rv-pe-empty">No pending expenses found</div>
//                       )}
//                     </div>
//                   )}

//                   <div className="rp-table-row rp-table-total">
//                     <div className="rp-col rp-col-label">
//                       <span className="rp-item-name rp-total-name">Total</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Total Due</span>
//                       <span className="rp-due-value rp-total-value">{formatCurrency(totalDue)}</span>
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Total Payable</span>
//                       <span className="rp-due-value rp-total-value">{formatCurrency(totalPayable)}</span>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="rp-extra-row">
//                   <div className="rp-field-group">
//                     <label className="rp-field-label">Payment Mode</label>
//                     <select
//                       className={`rp-select ${errors.PaymentMode ? 'rp-field-error' : ''}`}
//                       value={formData.PaymentMode}
//                       onChange={(e) => handleChange('PaymentMode', e.target.value)}
//                     >
//                       <option value="CASH">💵 Cash</option>
//                       <option value="UPI">📱 UPI</option>
//                       <option value="BANK">🏦 Bank Transfer</option>
//                       <option value="CHEQUE">📝 Cheque</option>
//                     </select>
//                     {errors.PaymentMode && <span className="rp-error-text">{errors.PaymentMode}</span>}
//                   </div>

//                   <div className="rp-field-group">
//                     <label className="rp-field-label">Reference No.</label>
//                     <input
//                       type="text"
//                       className="rp-text-input"
//                       placeholder="Enter reference number"
//                       value={formData.ReferenceNo}
//                       onChange={(e) => handleChange('ReferenceNo', e.target.value)}
//                     />
//                   </div>
//                 </div>

//                 <div className="rp-field-group rp-field-full">
//                   <label className="rp-field-label">Narration <span className="rp-optional">(Optional)</span></label>
//                   <input
//                     type="text"
//                     className="rp-text-input"
//                     placeholder="Enter narration"
//                     value={formData.Narration}
//                     onChange={(e) => handleChange('Narration', e.target.value)}
//                   />
//                 </div>

//                 {errors.general && <div className="rp-error-text rp-error-general">{errors.general}</div>}
//                 {message && <div className={`rp-message rp-message-${messageType}`}>{message}</div>}
//               </>
//             )}

//             {activeTab === 'security' && (
//               <SecurityDepositModal
//                 embedded
//                 depositData={securityDeposit}
//                 admissionId={bed?.admissionId}
//                 onClose={() => { }}
//                 onTransactionComplete={handleTransactionComplete}
//               />
//             )}

//             {activeTab === 'settlement' && (
//               <FullFinalSettlementSection
//                 admissionId={bed?.admissionId}
//                 onSettled={() => {
//                   if (onSuccess) onSuccess(bed.bedId, 'SETTLED');
//                 }}
//               />
//             )}
//           </AnimatedHeight>
//         </div>

//         {activeTab === 'overall' && (
//           <div className="rp-footer">
//             <button
//               type="button"
//               className="rp-btn rp-btn-secondary"
//               onClick={onClose}
//               disabled={isSubmitting}
//             >
//               Cancel
//             </button>
//             <button
//               type="button"
//               className="rp-btn rp-btn-primary"
//               onClick={handleSubmit}
//               disabled={isSubmitting || isFetchingReceipt}
//             >
//               {isSubmitting || isFetchingReceipt ? (
//                 <>
//                   <span className="rp-spinner"></span>
//                   Processing...
//                 </>
//               ) : (
//                 `Pay ${formatCurrency(totalPayable)}`
//               )}
//             </button>
//           </div>
//         )}
//       </div>

//       {showReceipt && receiptData && (
//         <div
//           className="rv-receipt-overlay"
//           onClick={() => {
//             setShowReceipt(false);
//             onClose();
//           }}
//         >
//           <div onClick={e => e.stopPropagation()}>
//             <RentReceipt
//               {...receiptData}
//               onClose={() => {
//                 setShowReceipt(false);
//                 onClose();
//               }}
//             />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// const OverallRentModal = ({
//   pgId,
//   bed,
//   pgName,
//   onClose,
//   onSuccess,
//   onSendReminder
// }) => {
//   const [activeTab, setActiveTab] = useState('overall') // 'overall' | 'security' | 'settlement'
//   const [expandedSection, setExpandedSection] = useState(null) // 'rent' | 'electricity' | 'expense' | null

//   const [formData, setFormData] = useState({
//     RentAmount: '',
//     ElectricityAmount: '',
//     ExpenseAmount: '',
//     PaymentMode: 'CASH',
//     ReferenceNo: '',
//     Narration: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [message, setMessage] = useState('');
//   const [messageType, setMessageType] = useState('success');
//   const [errors, setErrors] = useState({});

//   const [overallDueData, setOverallDueData] = useState(null);
//   const [isLoadingOverallDue, setIsLoadingOverallDue] = useState(false);

//   const [tenantStatement, setTenantStatement] = useState([]);
//   const [isLoadingStatement, setIsLoadingStatement] = useState(false);

//   const [securityDeposit, setSecurityDeposit] = useState(null);
//   const [isLoadingDeposit, setIsLoadingDeposit] = useState(false);

//   const [pendingExpenses, setPendingExpenses] = useState([]);
//   const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
//   const [currentAdmissionId, setCurrentAdmissionId] = useState(null);

//   const [isSendingReminder, setIsSendingReminder] = useState(false);

//   const [receiptData, setReceiptData] = useState(null);
//   const [showReceipt, setShowReceipt] = useState(false);
//   const [isFetchingReceipt, setIsFetchingReceipt] = useState(false);

//   // NEW: State for tenant advance
//   const [tenantAdvance, setTenantAdvance] = useState({
//     advanceDeposit: [],
//     securityDeposit: []
//   });
//   const [isLoadingAdvance, setIsLoadingAdvance] = useState(false);

//   const statusColors = getStatusColor(bed?.rentStatus);
//   const showReminder = ['DUE', 'OVERDUE', 'NOT PAID'].includes(bed?.rentStatus);

//   // ─── Fetchers ────────────────────────────────────────────────
//   const fetchOverallDue = async () => {
//     if (!bed?.admissionId) return;
//     setIsLoadingOverallDue(true);
//     try {
//       const response = await getOverallDue(bed.admissionId);
//       console.log("response", response);


//       if (response?.success && response?.data) {
//         setOverallDueData(response.data);
//       }
//     } catch (error) {
//       console.error('Error fetching overall due:', error);
//     } finally {
//       setIsLoadingOverallDue(false);
//     }
//   };

//   const fetchTenantStatement = async () => {
//     if (!bed?.admissionId || !pgId) return;
//     setIsLoadingStatement(true);
//     try {
//       const response = await getTenantStatement(bed.admissionId, pgId);
//       setTenantStatement(response || []);
//     } catch (err) {
//       console.error('Error fetching tenant statement:', err);
//       setTenantStatement([]);
//     } finally {
//       setIsLoadingStatement(false);
//     }
//   };

//   const fetchSecurityDeposit = async () => {
//     if (!bed?.admissionId) return;
//     setIsLoadingDeposit(true);
//     try {
//       const response = await getSecurityDeposit(bed.admissionId);
//       setSecurityDeposit(response?.data || null);
//     } catch (err) {
//       console.error('Error fetching security deposit:', err);
//       setSecurityDeposit(null);
//     } finally {
//       setIsLoadingDeposit(false);
//     }
//   };

//   // NEW: Fetch tenant advance
//   // const fetchTenantAdvance = async () => {
//   //   if (!bed?.admissionId) return;
//   //   setIsLoadingAdvance(true);
//   //   try {
//   //     const response = await getTenantAdvance(bed.admissionId);
//   //     console.log("response", response);

//   //     if (response?.success && response?.data) {
//   //       setTenantAdvance({
//   //         advanceDeposit: response.data.advanceDeposit || [],
//   //         securityDeposit: response.data.securityDeposit || []
//   //       });
//   //     } else {
//   //       setTenantAdvance({
//   //         advanceDeposit: [],
//   //         securityDeposit: []
//   //       });
//   //     }
//   //   } catch (err) {
//   //     console.error('Error fetching tenant advance:', err);
//   //     setTenantAdvance({
//   //       advanceDeposit: [],
//   //       securityDeposit: []
//   //     });
//   //   } finally {
//   //     setIsLoadingAdvance(false);
//   //   }
//   // };

//   const fetchTenantAdvance = async () => {
//   if (!bed?.admissionId) return;
//   setIsLoadingAdvance(true);
//   try {
//     const response = await getTenantAdvance(bed.admissionId);
//     console.log("response", response);

//     // Check if response has advanceDeposit directly (no wrapper)
//     if (response && response.advanceDeposit !== undefined) {
//       setTenantAdvance({
//         advanceDeposit: response.advanceDeposit || [],
//         securityDeposit: response.securityDeposit || []
//       });
//     } 
//     // If response is wrapped in { success, data }
//     else if (response?.success && response?.data) {
//       setTenantAdvance({
//         advanceDeposit: response.data.advanceDeposit || [],
//         securityDeposit: response.data.securityDeposit || []
//       });
//     } 
//     // If no data found
//     else {
//       setTenantAdvance({
//         advanceDeposit: [],
//         securityDeposit: []
//       });
//     }
//   } catch (err) {
//     console.error('Error fetching tenant advance:', err);
//     setTenantAdvance({
//       advanceDeposit: [],
//       securityDeposit: []
//     });
//   } finally {
//     setIsLoadingAdvance(false);
//   }
// };

//   const fetchPendingExpenses = async () => {
//     if (!bed?.admissionId || !pgId) return;
//     setCurrentAdmissionId(bed.admissionId);
//     setIsLoadingExpenses(true);
//     try {
//       const response = await getTenantPendingExpenses(pgId, bed.admissionId);
//       if (response?.data && Array.isArray(response.data)) {
//         setPendingExpenses(response.data);
//       } else {
//         setPendingExpenses([]);
//       }
//     } catch (err) {
//       console.error('Error fetching pending expenses:', err);
//       setPendingExpenses([]);
//     } finally {
//       setIsLoadingExpenses(false);
//     }
//   };

//   useEffect(() => {
//     if (bed?.admissionId) {
//       fetchOverallDue();
//       fetchSecurityDeposit();
//       fetchTenantAdvance(); // NEW: Fetch tenant advance
//     }
//   }, [bed?.admissionId]);

//   useEffect(() => {
//     if (bed?.admissionId && pgId) {
//       fetchTenantStatement();
//       fetchPendingExpenses();
//     }
//   }, [bed?.admissionId, pgId]);

//   const handleTransactionComplete = (updatedDeposit) => {
//     setSecurityDeposit(updatedDeposit);
//     if (onSuccess) {
//       onSuccess(bed.bedId, updatedDeposit?.Status);
//     }
//   };

//   // ─── WhatsApp reminder ───────────────────────────────────────
//   const sendWhatsAppReminder = async (bedData) => {
//     if (!bedData) return;
//     setIsSendingReminder(true);

//     try {
//       const customerName = bedData.name || 'Tenant';
//       const dueDate = bedData.DueDate || new Date().toISOString();
//       const billingMonth = new Date(dueDate).toLocaleString('en-US', { month: 'long', year: 'numeric' });
//       const roomNumber = bedData.RoomNumber || bedData.roomNumber || 'N/A';
//       const outstandingAmount = bedData.NetPayable || bedData.rent || 0;
//       const pgNameValue = pgName || 'PG';
//       const phoneNumbers = [`91${bedData.PhoneNumber}` || "916260216707"];

//       const payload = {
//         integrated_number: "918796203089",
//         content_type: "template",
//         payload: {
//           messaging_product: "whatsapp",
//           type: "template",
//           template: {
//             name: "aapkapg_overdue_final",
//             language: { code: "en", policy: "deterministic" },
//             namespace: "cbf3b2ba_dca4_40bd_9e8c_747d92ec2d3c",
//             to_and_components: [
//               {
//                 to: phoneNumbers,
//                 components: {
//                   body_customer_name: { type: "text", value: customerName, parameter_name: "customer_name" },
//                   body_billing_month: { type: "text", value: billingMonth, parameter_name: "billing_month" },
//                   body_room_number: { type: "text", value: roomNumber.toString(), parameter_name: "room_number" },
//                   body_outstanding_amount: { type: "text", value: outstandingAmount.toString(), parameter_name: "outstanding_amount" },
//                   body_due_date: {
//                     type: "text",
//                     value: new Date(dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
//                     parameter_name: "due_date"
//                   },
//                   body_pg_name: { type: "text", value: pgNameValue, parameter_name: "pg_name" }
//                 }
//               }
//             ]
//           }
//         }
//       };

//       const response = await sendWhatsAppOverdueReminder(payload);

//       if (response?.success) {
//         if (onSendReminder) onSendReminder(bedData);
//         alert(`✅ WhatsApp reminder sent to ${customerName}!`);
//       } else {
//         alert(`❌ Failed to send reminder: ${response?.message || 'Unknown error'}`);
//       }
//     } catch (error) {
//       console.error('Error sending WhatsApp reminder:', error);
//       const errorMessage = error.response?.data?.message || error.message || 'Failed to send WhatsApp reminder';
//       alert(`❌ Error: ${errorMessage}`);
//     } finally {
//       setIsSendingReminder(false);
//     }
//   };

//   const handleReminderClick = () => {
//     if (bed) {
//       sendWhatsAppReminder(bed);
//     }
//   };

//   // ─── Overall payment submit ──────────────────────────────────
//   const validate = () => {
//     const newErrors = {};
//     const { RentAmount, ElectricityAmount, ExpenseAmount, PaymentMode } = formData;

//     const rent = parseFloat(RentAmount) || 0;
//     const electricity = parseFloat(ElectricityAmount) || 0;
//     const expense = parseFloat(ExpenseAmount) || 0;

//     if (rent <= 0 && electricity <= 0 && expense <= 0) {
//       newErrors.general = 'At least one amount must be greater than 0';
//     }
//     if (!PaymentMode) {
//       newErrors.PaymentMode = 'Please select payment mode';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validate()) return;

//     setIsSubmitting(true);
//     setMessage('');

//     try {
//       const userId = localStorage.getItem('userId') || 1;

//       const payload = {
//         AdmissionID: bed?.admissionId,
//         RentAmount: parseFloat(formData.RentAmount) || 0,
//         ElectricityAmount: parseFloat(formData.ElectricityAmount) || 0,
//         ExpenseAmount: parseFloat(formData.ExpenseAmount) || 0,
//         PaymentMode: formData.PaymentMode,
//         DueinMonth: 0,
//         ReferenceNo: formData.ReferenceNo?.trim() || '',
//         UserId: parseInt(userId)
//       };

//       const response = await processOverallPayment(payload);
//       console.log("response", response)
//       if (response?.success) {
//         setMessage('Payment processed successfully!');
//         setMessageType('success');

//         fetchOverallDue();
//         fetchTenantStatement();
//         fetchPendingExpenses();
//         fetchTenantAdvance(); // Refresh advance data after payment

//         if (onSuccess) {
//           onSuccess(bed.bedId, '');
//         }

//         const receiptIdToFetch = response?.data?.Receipt?.ReceiptID || FIXED_RECEIPT_ID;
//         const receiptNoToFetch = FIXED_RECEIPT_NO;

//         try {
//           setIsFetchingReceipt(true);
//           const parsedReceipt = await fetchReceiptByNo(receiptIdToFetch);
//           setReceiptData(mapReceiptToRentReceiptProps(parsedReceipt));
//           setShowReceipt(true);
//         } catch (receiptErr) {
//           console.error('Error fetching receipt:', receiptErr);
//           setTimeout(() => onClose(), 1500);
//         } finally {
//           setIsFetchingReceipt(false);
//         }
//       } else {
//         throw new Error(response?.message || 'Payment failed');
//       }
//     } catch (error) {
//       console.error('Error processing overall payment:', error);
//       setMessage(error.message || 'Payment failed. Please try again.');
//       setMessageType('error');
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleChange = (field, value) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//     if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
//     if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
//   };

//   // Calculate advance deposit balance
//   const getAdvanceBalance = () => {
//     if (tenantAdvance.advanceDeposit && tenantAdvance.advanceDeposit.length > 0) {
//       return tenantAdvance.advanceDeposit[0].Balance || 0;
//     }
//     return 0;
//   };

//   const advanceBalance = getAdvanceBalance();

//   // Calculate adjusted rent due (rent due - advance balance)
//   const rentDue = overallDueData?.RentNetPayable || 0;
//   const adjustedRentDue = Math.max(0, rentDue - advanceBalance);

//   const electricityDue = overallDueData?.ElectricityNetPayable || 0;
//   const expenseDue = overallDueData?.ExpenseNetPayable || 0;
//   const totalDue = overallDueData?.TotalDue ?? (rentDue + electricityDue + expenseDue);
//   const adjustedTotalDue = overallDueData?.TotalDue 
//     ? Math.max(0, overallDueData.TotalDue - advanceBalance)
//     : Math.max(0, (rentDue + electricityDue + expenseDue) - advanceBalance);

//   const totalPayable =
//     (parseFloat(formData.RentAmount) || 0) +
//     (parseFloat(formData.ElectricityAmount) || 0) +
//     (parseFloat(formData.ExpenseAmount) || 0);

//   const toggleSection = (section) => {
//     setExpandedSection(prev => (prev === section ? null : section));
//   };

//   if (!bed) return null;

//   return (
//     <div className="rp-overlay" onClick={onClose}>
//       <div className="rp-modal" onClick={e => e.stopPropagation()}>
//         <div className="rp-header">
//           <div>
//             <h3 className="rp-title">{bed.name || 'Tenant'}</h3>
//             <p className="rp-subtitle">
//               <span className="rv-rp-badge" style={{ background: statusColors.bg, color: statusColors.color }}>
//                 <span className="rv-rp-dot" style={{ background: statusColors.dot }} />
//                 {bed.rentStatus === 'NOT PAID' ? 'NOT PAID' : (bed.rentStatus || 'NA')}
//               </span>
//               {/* NEW: Show advance balance badge */}
//               {advanceBalance > 0 && (
//                 <span className="rv-advance-badge" style={{ 
//                   background: '#e8f5e9', 
//                   color: '#2e7d32',
//                   marginLeft: '8px',
//                   padding: '2px 10px',
//                   borderRadius: '12px',
//                   fontSize: '12px',
//                   fontWeight: '600'
//                 }}>
//                   Advance: ₹{advanceBalance.toLocaleString('en-IN')}
//                 </span>
//               )}
//             </p>
//           </div>
//           <div className="rp-header-actions">
//             {showReminder && (
//               <button
//                 className="rv-rp-reminder-btn"
//                 onClick={handleReminderClick}
//                 disabled={isSendingReminder}
//                 title="Send WhatsApp reminder"
//               >
//                 {isSendingReminder ? (
//                   <>
//                     <span className="rv-spinner-small"></span>
//                     Sending...
//                   </>
//                 ) : (
//                   <>
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                       <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
//                       <path d="M13.73 21a2 2 0 01-3.46 0" />
//                     </svg>
//                     Send Reminder
//                   </>
//                 )}
//               </button>
//             )}
//             <button className="rp-close" onClick={onClose} aria-label="Close">
//               <IconClose />
//             </button>
//           </div>
//         </div>

//         {/* ─── Tabs ─────────────────────────────────────────── */}
//         <div className="rp-tabs">
//           <button
//             className={`rp-tab ${activeTab === 'overall' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('overall')}
//           >
//             Regular Payment
//           </button>
//           <button
//             className={`rp-tab ${activeTab === 'security' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('security')}
//           >
//             Security Deposit
//           </button>
//           <button
//             className={`rp-tab ${activeTab === 'settlement' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('settlement')}
//           >
//             Full and Final Payment
//           </button>
//         </div>

//         <div className="rp-body">
//           <AnimatedHeight>
//             {activeTab === 'overall' && (
//               <>
//                 {/* NEW: Advance Deposit Info Banner */}
//                 {advanceBalance > 0 && (
//                   <div className="rv-advance-banner" style={{
//                     background: '#e3f2fd',
//                     border: '1px solid #90caf9',
//                     borderRadius: '8px',
//                     padding: '10px 16px',
//                     marginBottom: '16px',
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'center'
//                   }}>
//                     <div>
//                       <span style={{ fontWeight: '600', color: '#1565c0' }}>💳 Advance Deposit Available</span>
//                       <span style={{ marginLeft: '12px', color: '#0d47a1' }}>
//                         ₹{advanceBalance.toLocaleString('en-IN')}
//                       </span>
//                     </div>
//                     <div style={{ fontSize: '13px', color: '#0d47a1' }}>
//                       Adjusted from rent due
//                     </div>
//                   </div>
//                 )}

//                 <div className="rp-table">
//                   <div className="rp-table-row rp-table-head">
//                     <div className="rp-col rp-col-label"></div>
//                     <div className="rp-col rp-col-due">Due Summary</div>
//                     <div className="rp-col rp-col-pay">Payment Details</div>
//                   </div>

//                   {/* Rent Row - Updated with advance adjustment */}
//                   <div className="rp-table-row rp-history-row">
//                     <div
//                       className="rp-col rp-col-label rp-col-clickable"
//                       onClick={() => toggleSection('rent')}
//                     >
//                       <span className="rp-icon rp-icon-rent"><IconHouse /></span>
//                       <span className="rp-item-name">Rent</span>
//                       <span className={`rp-history-caret ${expandedSection === 'rent' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Due</span>
//                       <span className="rp-due-value">{formatCurrency(adjustedRentDue)}</span>
//                       {/* Show adjusted due if advance is applied */}
//                       {advanceBalance > 0 && rentDue > 0 && (
//                         <div style={{ fontSize: '11px', color: '#2e7d32', marginTop: '2px' }}>
//                           Before advance adjust: {formatCurrency(rentDue)}
//                         </div>
//                       )}
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Pay</span>
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.RentAmount}
//                           onChange={(e) => handleChange('RentAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                   {expandedSection === 'rent' && (
//                     <div className="rp-history-panel">
//                       {isLoadingStatement ? (
//                         <div className="rv-statement-loading">
//                           <span className="rv-spinner-small"></span> Loading rent history...
//                         </div>
//                       ) : tenantStatement.length > 0 ? (
//                         <div className="rv-statement-table-wrapper">
//                           <table className="rv-statement-table">
//                             <thead>
//                               <tr>
//                                 <th>Month</th>
//                                 <th>Year</th>
//                                 <th>Monthly Rent (₹)</th>
//                                 <th>Rent Due (₹)</th>
//                                 <th>Paid (₹)</th>
//                                 <th>Advance Adjusted (₹)</th>
//                                 <th>Net Payable (₹)</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {tenantStatement.map((item, idx) => (
//                                 <tr key={idx}>
//                                   <td>{item.MonthName}</td>
//                                   <td>{item.InvoiceYear}</td>
//                                   <td>{(item.RentAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td>{(item.TotalAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-paid-amount">{(item.PaidAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-adjusted-amount">{(item.AdvanceAdjusted || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-netpayable-amount">{(item.NetPayable || 0).toLocaleString('en-IN')}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <div className="rv-pe-empty">No rent history found</div>
//                       )}
//                     </div>
//                   )}

//                   {/* Electricity Row - Updated */}
//                   <div className="rp-table-row rp-history-row">
//                     <div
//                       className="rp-col rp-col-label rp-col-clickable"
//                       onClick={() => toggleSection('electricity')}
//                     >
//                       <span className="rp-icon rp-icon-electricity"><IconBolt /></span>
//                       <span className="rp-item-name">Electricity</span>
//                       <span className={`rp-history-caret ${expandedSection === 'electricity' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Due</span>
//                       <span className="rp-due-value">{formatCurrency(electricityDue)}</span>
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Pay</span>
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.ElectricityAmount}
//                           onChange={(e) => handleChange('ElectricityAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                   {expandedSection === 'electricity' && (
//                     <div className="rp-history-panel">
//                       <div className="rv-statement-table-wrapper">
//                         <table className="rv-statement-table">
//                           <thead>
//                             <tr>
//                               <th>Month</th>
//                               <th>Year</th>
//                               <th>Monthly Electricity (₹)</th>
//                               <th>Payment Due (₹)</th>
//                               <th>Paid (₹)</th>
//                               <th>Security Adjusted (₹)</th>
//                               <th>Net Payable (₹)</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {(tenantStatement.length > 0 ? tenantStatement : [{}]).map((item, idx) => (
//                               <tr key={idx}>
//                                 <td>{item.MonthName || '-'}</td>
//                                 <td>{item.InvoiceYear || '-'}</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   )}

//                   {/* Other Expenses Row */}
//                   <div className="rp-table-row rp-history-row">
//                     <div
//                       className="rp-col rp-col-label rp-col-clickable"
//                       onClick={() => toggleSection('expense')}
//                     >
//                       <span className="rp-icon rp-icon-expense"><IconDoc /></span>
//                       <span className="rp-item-name">Other Expenses</span>
//                       <span className={`rp-history-caret ${expandedSection === 'expense' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Due</span>
//                       <span className="rp-due-value">{formatCurrency(expenseDue)}</span>
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Pay</span>
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.ExpenseAmount}
//                           onChange={(e) => handleChange('ExpenseAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                   {expandedSection === 'expense' && (
//                     <div className="rp-history-panel">
//                       {isLoadingExpenses ? (
//                         <div className="rv-statement-loading">
//                           <span className="rv-spinner-small"></span> Loading expenses...
//                         </div>
//                       ) : pendingExpenses.length > 0 ? (
//                         <div className="rv-pe-table-wrapper">
//                           <table className="rv-pe-table">
//                             <thead>
//                               <tr>
//                                 <th>Expense</th>
//                                 <th>Expense Date</th>
//                                 <th>Amount</th>
//                                 <th>Adjusted Amount</th>
//                                 <th>Paid Amount</th>
//                                 <th>Net Payable</th>
//                                 <th>Description</th>
//                                 <th>Status</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {pendingExpenses.map((expense) => (
//                                 <tr key={expense.ExpenseID}>
//                                   <td>{expense.LedgerName}</td>
//                                   <td>{new Date(expense.ExpenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
//                                   <td className="rv-pe-amount">{fmt(expense.Amount)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.AdvanceAdjusted || 0)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.PaidAmount || 0)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.NetPayable || 0)}</td>
//                                   <td>{expense.Description || '-'}</td>
//                                   <td>{expense.Status || '-'}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <div className="rv-pe-empty">No pending expenses found</div>
//                       )}
//                     </div>
//                   )}

//                   <div className="rp-table-row rp-table-total">
//                     <div className="rp-col rp-col-label">
//                       <span className="rp-item-name rp-total-name">Total</span>
//                     </div>
//                     <div className="rp-col rp-col-due">
//                       <span className="rp-due-label">Total Due</span>
//                       <span className="rp-due-value rp-total-value">{formatCurrency(adjustedTotalDue)}</span>
//                       {/* Show adjusted total if advance is applied */}
//                       {advanceBalance > 0 && totalDue > 0 && (
//                         <div style={{ fontSize: '12px', color: '#2e7d32', marginTop: '2px' }}>
//                           Before advance adjust: {formatCurrency(totalDue)}
//                         </div>
//                       )}
//                     </div>
//                     <div className="rp-col rp-col-pay">
//                       <span className="rp-pay-label">Total Payable</span>
//                       <span className="rp-due-value rp-total-value">{formatCurrency(totalPayable)}</span>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="rp-extra-row">
//                   <div className="rp-field-group">
//                     <label className="rp-field-label">Payment Mode</label>
//                     <select
//                       className={`rp-select ${errors.PaymentMode ? 'rp-field-error' : ''}`}
//                       value={formData.PaymentMode}
//                       onChange={(e) => handleChange('PaymentMode', e.target.value)}
//                     >
//                       <option value="CASH">💵 Cash</option>
//                       <option value="UPI">📱 UPI</option>
//                       <option value="BANK">🏦 Bank Transfer</option>
//                       <option value="CHEQUE">📝 Cheque</option>
//                     </select>
//                     {errors.PaymentMode && <span className="rp-error-text">{errors.PaymentMode}</span>}
//                   </div>

//                   <div className="rp-field-group">
//                     <label className="rp-field-label">Reference No.</label>
//                     <input
//                       type="text"
//                       className="rp-text-input"
//                       placeholder="Enter reference number"
//                       value={formData.ReferenceNo}
//                       onChange={(e) => handleChange('ReferenceNo', e.target.value)}
//                     />
//                   </div>
//                 </div>

//                 <div className="rp-field-group rp-field-full">
//                   <label className="rp-field-label">Narration <span className="rp-optional">(Optional)</span></label>
//                   <input
//                     type="text"
//                     className="rp-text-input"
//                     placeholder="Enter narration"
//                     value={formData.Narration}
//                     onChange={(e) => handleChange('Narration', e.target.value)}
//                   />
//                 </div>

//                 {errors.general && <div className="rp-error-text rp-error-general">{errors.general}</div>}
//                 {message && <div className={`rp-message rp-message-${messageType}`}>{message}</div>}
//               </>
//             )}

//             {activeTab === 'security' && (
//               <SecurityDepositModal
//                 embedded
//                 depositData={securityDeposit}
//                 admissionId={bed?.admissionId}
//                 onClose={() => { }}
//                 onTransactionComplete={handleTransactionComplete}
//               />
//             )}

//             {activeTab === 'settlement' && (
//               <FullFinalSettlementSection
//                 admissionId={bed?.admissionId}
//                 onSettled={() => {
//                   if (onSuccess) onSuccess(bed.bedId, 'SETTLED');
//                 }}
//               />
//             )}
//           </AnimatedHeight>
//         </div>

//         {activeTab === 'overall' && (
//           <div className="rp-footer">
//             <button
//               type="button"
//               className="rp-btn rp-btn-secondary"
//               onClick={onClose}
//               disabled={isSubmitting}
//             >
//               Cancel
//             </button>
//             <button
//               type="button"
//               className="rp-btn rp-btn-primary"
//               onClick={handleSubmit}
//               disabled={isSubmitting || isFetchingReceipt}
//             >
//               {isSubmitting || isFetchingReceipt ? (
//                 <>
//                   <span className="rp-spinner"></span>
//                   Processing...
//                 </>
//               ) : (
//                 `Pay ${formatCurrency(totalPayable)}`
//               )}
//             </button>
//           </div>
//         )}
//       </div>

//       {showReceipt && receiptData && (
//         <div
//           className="rv-receipt-overlay"
//           onClick={() => {
//             setShowReceipt(false);
//             onClose();
//           }}
//         >
//           <div onClick={e => e.stopPropagation()}>
//             <RentReceipt
//               {...receiptData}
//               onClose={() => {
//                 setShowReceipt(false);
//                 onClose();
//               }}
//             />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// ─── OwnerRevenue main component ──────────────────────────────────

// const OverallRentModal = ({
//   pgId,
//   bed,
//   pgName,
//   onClose,
//   onSuccess,
//   onSendReminder
// }) => {
//   const [activeTab, setActiveTab] = useState('overall') // 'overall' | 'security' | 'settlement'
//   const [expandedSection, setExpandedSection] = useState(null) // 'rent' | 'electricity' | 'expense' | null

//   const [formData, setFormData] = useState({
//     RentAmount: '',
//     ElectricityAmount: '',
//     ExpenseAmount: '',
//     PaymentMode: 'CASH',
//     ReferenceNo: '',
//     Narration: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [message, setMessage] = useState('');
//   const [messageType, setMessageType] = useState('success');
//   const [errors, setErrors] = useState({});

//   const [overallDueData, setOverallDueData] = useState(null);
//   const [isLoadingOverallDue, setIsLoadingOverallDue] = useState(false);

//   const [tenantStatement, setTenantStatement] = useState([]);
//   const [isLoadingStatement, setIsLoadingStatement] = useState(false);

//   const [securityDeposit, setSecurityDeposit] = useState(null);
//   const [isLoadingDeposit, setIsLoadingDeposit] = useState(false);

//   const [pendingExpenses, setPendingExpenses] = useState([]);
//   const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
//   const [currentAdmissionId, setCurrentAdmissionId] = useState(null);

//   const [isSendingReminder, setIsSendingReminder] = useState(false);

//   const [receiptData, setReceiptData] = useState(null);
//   const [showReceipt, setShowReceipt] = useState(false);
//   const [isFetchingReceipt, setIsFetchingReceipt] = useState(false);

//   const statusColors = getStatusColor(bed?.rentStatus);
//   const showReminder = ['DUE', 'OVERDUE', 'NOT PAID'].includes(bed?.rentStatus);

//   // ─── Fetchers ────────────────────────────────────────────────
//   const fetchOverallDue = async () => {
//     if (!bed?.admissionId) return;
//     setIsLoadingOverallDue(true);
//     try {
//       const response = await getOverallDue(bed.admissionId);
//       console.log("response", response);

//       if (response?.success && response?.data) {
//         // Transform the data to match the expected structure
//         const data = response.data;
//         setOverallDueData({
//           RentNetPayable: data.RentDue || 0,
//           ElectricityNetPayable: data.ElectricityDue || 0,
//           ExpenseNetPayable: data.ExpenseDue || 0,
//           TotalDue: data.TotalPayable || 0,
//           AdvanceAvailable: data.AdvanceAvailable || 0,
//           AdvanceAdjusted: data.AdvanceAdjusted || 0,
//           TotalPayable: data.TotalPayable || 0,
//           // Store raw data for display
//           rawData: data
//         });
//       }
//     } catch (error) {
//       console.error('Error fetching overall due:', error);
//     } finally {
//       setIsLoadingOverallDue(false);
//     }
//   };

//   const fetchTenantStatement = async () => {
//     if (!bed?.admissionId || !pgId) return;
//     setIsLoadingStatement(true);
//     try {
//       const response = await getTenantStatement(bed.admissionId, pgId);
//       setTenantStatement(response || []);
//     } catch (err) {
//       console.error('Error fetching tenant statement:', err);
//       setTenantStatement([]);
//     } finally {
//       setIsLoadingStatement(false);
//     }
//   };

//   const fetchSecurityDeposit = async () => {
//     if (!bed?.admissionId) return;
//     setIsLoadingDeposit(true);
//     try {
//       const response = await getSecurityDeposit(bed.admissionId);
//       setSecurityDeposit(response?.data || null);
//     } catch (err) {
//       console.error('Error fetching security deposit:', err);
//       setSecurityDeposit(null);
//     } finally {
//       setIsLoadingDeposit(false);
//     }
//   };

//   const fetchPendingExpenses = async () => {
//     if (!bed?.admissionId || !pgId) return;
//     setCurrentAdmissionId(bed.admissionId);
//     setIsLoadingExpenses(true);
//     try {
//       const response = await getTenantPendingExpenses(pgId, bed.admissionId);
//       if (response?.data && Array.isArray(response.data)) {
//         setPendingExpenses(response.data);
//       } else {
//         setPendingExpenses([]);
//       }
//     } catch (err) {
//       console.error('Error fetching pending expenses:', err);
//       setPendingExpenses([]);
//     } finally {
//       setIsLoadingExpenses(false);
//     }
//   };

//   // Add this near the top with other state variables
// const [tenantAdvance, setTenantAdvance] = useState({
//   advanceDeposit: [],
//   securityDeposit: []
// });
// const [isLoadingAdvance, setIsLoadingAdvance] = useState(false);

// // Add this fetcher function
// const fetchTenantAdvance = async () => {
//   if (!bed?.admissionId) return;
//   setIsLoadingAdvance(true);
//   try {
//     const response = await getTenantAdvance(bed.admissionId);
//     console.log("Advance response", response);

//     if (response && response.advanceDeposit !== undefined) {
//       setTenantAdvance({
//         advanceDeposit: response.advanceDeposit || [],
//         securityDeposit: response.securityDeposit || []
//       });
//     } else if (response?.success && response?.data) {
//       setTenantAdvance({
//         advanceDeposit: response.data.advanceDeposit || [],
//         securityDeposit: response.data.securityDeposit || []
//       });
//     } else {
//       setTenantAdvance({
//         advanceDeposit: [],
//         securityDeposit: []
//       });
//     }
//   } catch (err) {
//     console.error('Error fetching tenant advance:', err);
//     setTenantAdvance({
//       advanceDeposit: [],
//       securityDeposit: []
//     });
//   } finally {
//     setIsLoadingAdvance(false);
//   }
// };

//   useEffect(() => {
//     if (bed?.admissionId) {
//       fetchOverallDue();
//       fetchSecurityDeposit();
//       fetchTenantAdvance(); 
//     }
//   }, [bed?.admissionId]);

//   useEffect(() => {
//     if (bed?.admissionId && pgId) {
//       fetchTenantStatement();
//       fetchPendingExpenses();
//     }
//   }, [bed?.admissionId, pgId]);

//   const handleTransactionComplete = (updatedDeposit) => {
//     setSecurityDeposit(updatedDeposit);
//     if (onSuccess) {
//       onSuccess(bed.bedId, updatedDeposit?.Status);
//     }
//   };

//   // ─── WhatsApp reminder ───────────────────────────────────────
//   const sendWhatsAppReminder = async (bedData) => {
//     if (!bedData) return;
//     setIsSendingReminder(true);

//     try {
//       const customerName = bedData.name || 'Tenant';
//       const dueDate = bedData.DueDate || new Date().toISOString();
//       const billingMonth = new Date(dueDate).toLocaleString('en-US', { month: 'long', year: 'numeric' });
//       const roomNumber = bedData.RoomNumber || bedData.roomNumber || 'N/A';
//       const outstandingAmount = bedData.NetPayable || bedData.rent || 0;
//       const pgNameValue = pgName || 'PG';
//       const phoneNumbers = [`91${bedData.PhoneNumber}` || "916260216707"];

//       const payload = {
//         integrated_number: "918796203089",
//         content_type: "template",
//         payload: {
//           messaging_product: "whatsapp",
//           type: "template",
//           template: {
//             name: "aapkapg_overdue_final",
//             language: { code: "en", policy: "deterministic" },
//             namespace: "cbf3b2ba_dca4_40bd_9e8c_747d92ec2d3c",
//             to_and_components: [
//               {
//                 to: phoneNumbers,
//                 components: {
//                   body_customer_name: { type: "text", value: customerName, parameter_name: "customer_name" },
//                   body_billing_month: { type: "text", value: billingMonth, parameter_name: "billing_month" },
//                   body_room_number: { type: "text", value: roomNumber.toString(), parameter_name: "room_number" },
//                   body_outstanding_amount: { type: "text", value: outstandingAmount.toString(), parameter_name: "outstanding_amount" },
//                   body_due_date: {
//                     type: "text",
//                     value: new Date(dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
//                     parameter_name: "due_date"
//                   },
//                   body_pg_name: { type: "text", value: pgNameValue, parameter_name: "pg_name" }
//                 }
//               }
//             ]
//           }
//         }
//       };

//       const response = await sendWhatsAppOverdueReminder(payload);

//       if (response?.success) {
//         if (onSendReminder) onSendReminder(bedData);
//         alert(`✅ WhatsApp reminder sent to ${customerName}!`);
//       } else {
//         alert(`❌ Failed to send reminder: ${response?.message || 'Unknown error'}`);
//       }
//     } catch (error) {
//       console.error('Error sending WhatsApp reminder:', error);
//       const errorMessage = error.response?.data?.message || error.message || 'Failed to send WhatsApp reminder';
//       alert(`❌ Error: ${errorMessage}`);
//     } finally {
//       setIsSendingReminder(false);
//     }
//   };

//   const handleReminderClick = () => {
//     if (bed) {
//       sendWhatsAppReminder(bed);
//     }
//   };

//   // ─── Overall payment submit ──────────────────────────────────
//   const validate = () => {
//     const newErrors = {};
//     const { RentAmount, ElectricityAmount, ExpenseAmount, PaymentMode } = formData;

//     const rent = parseFloat(RentAmount) || 0;
//     const electricity = parseFloat(ElectricityAmount) || 0;
//     const expense = parseFloat(ExpenseAmount) || 0;

//     if (rent <= 0 && electricity <= 0 && expense <= 0) {
//       newErrors.general = 'At least one amount must be greater than 0';
//     }
//     if (!PaymentMode) {
//       newErrors.PaymentMode = 'Please select payment mode';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // const handleSubmit = async () => {
//   //   if (!validate()) return;

//   //   setIsSubmitting(true);
//   //   setMessage('');

//   //   try {
//   //     const userId = localStorage.getItem('userId') || 1;

//   //     const payload = {
//   //       AdmissionID: bed?.admissionId,
//   //       RentAmount: parseFloat(formData.RentAmount) || 0,
//   //       ElectricityAmount: parseFloat(formData.ElectricityAmount) || 0,
//   //       ExpenseAmount: parseFloat(formData.ExpenseAmount) || 0,
//   //       PaymentMode: formData.PaymentMode,
//   //       DueinMonth: 0,
//   //       ReferenceNo: formData.ReferenceNo?.trim() || '',
//   //       UserId: parseInt(userId)
//   //     };

//   //     const response = await processOverallPayment(payload);
//   //     console.log("response", response)
//   //     if (response?.success) {
//   //       setMessage('Payment processed successfully!');
//   //       setMessageType('success');

//   //       fetchOverallDue();
//   //       fetchTenantStatement();
//   //       fetchPendingExpenses();

//   //       if (onSuccess) {
//   //         onSuccess(bed.bedId, '');
//   //       }

//   //       const receiptIdToFetch = response?.data?.Receipt?.ReceiptID || FIXED_RECEIPT_ID;
//   //       const receiptNoToFetch = FIXED_RECEIPT_NO;

//   //       try {
//   //         setIsFetchingReceipt(true);
//   //         const parsedReceipt = await fetchReceiptByNo(receiptIdToFetch);
//   //         setReceiptData(mapReceiptToRentReceiptProps(parsedReceipt));
//   //         setShowReceipt(true);
//   //       } catch (receiptErr) {
//   //         console.error('Error fetching receipt:', receiptErr);
//   //         setTimeout(() => onClose(), 1500);
//   //       } finally {
//   //         setIsFetchingReceipt(false);
//   //       }
//   //     } else {
//   //       throw new Error(response?.message || 'Payment failed');
//   //     }
//   //   } catch (error) {
//   //     console.error('Error processing overall payment:', error);
//   //     setMessage(error.message || 'Payment failed. Please try again.');
//   //     setMessageType('error');
//   //   } finally {
//   //     setIsSubmitting(false);
//   //   }
//   // };


//   const handleSubmit = async () => {
//   if (!validate()) return;

//   setIsSubmitting(true);
//   setMessage('');

//   try {
//     const userId = localStorage.getItem('userId') || 1;

//     const payload = {
//       AdmissionID: bed?.admissionId,
//       RentAmount: parseFloat(formData.RentAmount) || 0,
//       ElectricityAmount: parseFloat(formData.ElectricityAmount) || 0,
//       ExpenseAmount: parseFloat(formData.ExpenseAmount) || 0,
//       PaymentMode: formData.PaymentMode,
//       DueinMonth: 0,
//       ReferenceNo: formData.ReferenceNo?.trim() || '',
//       UserId: parseInt(userId)
//     };

//     const response = await processOverallPayment(payload);
//     console.log("response", response)
//     if (response?.success) {
//       setMessage('Payment processed successfully!');
//       setMessageType('success');

//       fetchOverallDue();
//       fetchTenantStatement();
//       fetchPendingExpenses();
//       fetchTenantAdvance(); // Refresh advance data

//       if (onSuccess) {
//         onSuccess(bed.bedId, '');
//       }

//       const receiptIdToFetch = response?.data?.Receipt?.ReceiptID || FIXED_RECEIPT_ID;

//       try {
//         setIsFetchingReceipt(true);
//         const parsedReceipt = await fetchReceiptByNo(receiptIdToFetch);

//         // Fetch advance data to include in receipt
//         const advanceResponse = await getTenantAdvance(bed.admissionId);
//         let advanceData = null;
//         if (advanceResponse && advanceResponse.advanceDeposit !== undefined) {
//           advanceData = advanceResponse;
//         } else if (advanceResponse?.success && advanceResponse?.data) {
//           advanceData = advanceResponse.data;
//         }

//         // Pass advance data to receipt mapper
//         setReceiptData(mapReceiptToRentReceiptProps(parsedReceipt, advanceData));
//         setShowReceipt(true);
//       } catch (receiptErr) {
//         console.error('Error fetching receipt:', receiptErr);
//         setTimeout(() => onClose(), 1500);
//       } finally {
//         setIsFetchingReceipt(false);
//       }
//     } else {
//       throw new Error(response?.message || 'Payment failed');
//     }
//   } catch (error) {
//     console.error('Error processing overall payment:', error);
//     setMessage(error.message || 'Payment failed. Please try again.');
//     setMessageType('error');
//   } finally {
//     setIsSubmitting(false);
//   }
// };


//   const handleChange = (field, value) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//     if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
//     if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
//   };

//   const rentDue = overallDueData?.RentNetPayable || 0;
//   const electricityDue = overallDueData?.ElectricityNetPayable || 0;
//   const expenseDue = overallDueData?.ExpenseNetPayable || 0;
//   const totalDue = overallDueData?.TotalDue ?? (rentDue + electricityDue + expenseDue);
//   const advanceUsed = overallDueData?.AdvanceAdjusted || 0;

//   const totalPayable =
//     (parseFloat(formData.RentAmount) || 0) +
//     (parseFloat(formData.ElectricityAmount) || 0) +
//     (parseFloat(formData.ExpenseAmount) || 0);

//   const toggleSection = (section) => {
//     setExpandedSection(prev => (prev === section ? null : section));
//   };

//   if (!bed) return null;

//   return (
//     <div className="rp-overlay" onClick={onClose}>
//       <div className="rp-modal" onClick={e => e.stopPropagation()}>
//         <div className="rp-header">
//           <div>
//             <h3 className="rp-title">{bed.name || 'Tenant'}</h3>
//             <p className="rp-subtitle">
//               <span className="rv-rp-badge" style={{ background: statusColors.bg, color: statusColors.color }}>
//                 <span className="rv-rp-dot" style={{ background: statusColors.dot }} />
//                 {bed.rentStatus === 'NOT PAID' ? 'NOT PAID' : (bed.rentStatus || 'NA')}
//               </span>
//             </p>
//           </div>
//           <div className="rp-header-actions">
//             {showReminder && (
//               <button
//                 className="rv-rp-reminder-btn"
//                 onClick={handleReminderClick}
//                 disabled={isSendingReminder}
//                 title="Send WhatsApp reminder"
//               >
//                 {isSendingReminder ? (
//                   <>
//                     <span className="rv-spinner-small"></span>
//                     Sending...
//                   </>
//                 ) : (
//                   <>
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                       <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
//                       <path d="M13.73 21a2 2 0 01-3.46 0" />
//                     </svg>
//                     Send Reminder
//                   </>
//                 )}
//               </button>
//             )}
//             <button className="rp-close" onClick={onClose} aria-label="Close">
//               <IconClose />
//             </button>
//           </div>
//         </div>

//         {/* ─── Tabs ─────────────────────────────────────────── */}
//         <div className="rp-tabs">
//           <button
//             className={`rp-tab ${activeTab === 'overall' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('overall')}
//           >
//             Regular Payment
//           </button>
//           <button
//             className={`rp-tab ${activeTab === 'security' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('security')}
//           >
//             Security Deposit
//           </button>
//           <button
//             className={`rp-tab ${activeTab === 'settlement' ? 'rp-tab-active' : ''}`}
//             onClick={() => setActiveTab('settlement')}
//           >
//             Full and Final Payment
//           </button>
//         </div>

//         <div className="rp-body">
//           <AnimatedHeight>
//             {activeTab === 'overall' && (
//               <>
//                 {/* ─── Table Design ─────────────────────────────────── */}
//                 <div className="rp-table-wrapper">
//                   {/* Table Header */}
//                   <div className="rp-table-header">
//                     <div className="rp-th">Head</div>
//                     <div className="rp-th">Total Due</div>
//                     <div className="rp-th">Advance Used</div>
//                     <div className="rp-th">Paid Now (You Pay)</div>
//                     <div className="rp-th">Amount Received (Posted to this head)</div>
//                   </div>

//                   {/* Rent Row */}
//                   <div className="rp-table-row-custom" onClick={() => toggleSection('rent')}>
//                     <div className="rp-td rp-td-label">
//                       <span className="rp-icon rp-icon-rent"><IconHouse /></span>
//                       <span className="rp-item-name">Rent</span>
//                       <span className={`rp-history-caret ${expandedSection === 'rent' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-td rp-td-amount" data-label="Total Due">{formatCurrency(rentDue)}</div>
//                     <div className="rp-td rp-td-advance" data-label="Advance Used">{formatCurrency(advanceUsed)}</div>
//                     <div className="rp-td rp-td-pay" data-label="Paid Now">
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.RentAmount}
//                           onChange={(e) => handleChange('RentAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                     <div className="rp-td rp-td-received" data-label="Amount Received">{formatCurrency(parseFloat(formData.RentAmount) || 0)}</div>
//                   </div>

//                   {/* Rent History Panel */}
//                   {expandedSection === 'rent' && (
//                     <div className="rp-history-panel">
//                       {isLoadingStatement ? (
//                         <div className="rv-statement-loading">
//                           <span className="rv-spinner-small"></span> Loading rent history...
//                         </div>
//                       ) : tenantStatement.length > 0 ? (
//                         <div className="rv-statement-table-wrapper">
//                           <table className="rv-statement-table">
//                             <thead>
//                               <tr>
//                                 <th>Month</th>
//                                 <th>Year</th>
//                                 <th>Monthly Rent (₹)</th>
//                                 <th>Rent Due (₹)</th>
//                                 <th>Paid (₹)</th>
//                                 <th>Security Adjusted (₹)</th>
//                                 <th>Net Payable (₹)</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {tenantStatement.map((item, idx) => (
//                                 <tr key={idx}>
//                                   <td>{item.MonthName}</td>
//                                   <td>{item.InvoiceYear}</td>
//                                   <td>{(item.RentAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td>{(item.TotalAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-paid-amount">{(item.PaidAmount || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-adjusted-amount">{(item.AdvanceAdjusted || 0).toLocaleString('en-IN')}</td>
//                                   <td className="rv-netpayable-amount">{(item.NetPayable || 0).toLocaleString('en-IN')}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <div className="rv-pe-empty">No rent history found</div>
//                       )}
//                     </div>
//                   )}

//                   {/* Electricity Row */}
//                   <div className="rp-table-row-custom" onClick={() => toggleSection('electricity')}>
//                     <div className="rp-td rp-td-label">
//                       <span className="rp-icon rp-icon-electricity"><IconBolt /></span>
//                       <span className="rp-item-name">Electricity</span>
//                       <span className={`rp-history-caret ${expandedSection === 'electricity' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-td rp-td-amount" data-label="Total Due">{formatCurrency(electricityDue)}</div>
//                     <div className="rp-td rp-td-advance" data-label="Advance Used">₹0</div>
//                     <div className="rp-td rp-td-pay" data-label="Paid Now">
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.ElectricityAmount}
//                           onChange={(e) => handleChange('ElectricityAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                     <div className="rp-td rp-td-received" data-label="Amount Received">{formatCurrency(parseFloat(formData.ElectricityAmount) || 0)}</div>
//                   </div>

//                   {/* Electricity History Panel */}
//                   {expandedSection === 'electricity' && (
//                     <div className="rp-history-panel">
//                       <div className="rv-statement-table-wrapper">
//                         <table className="rv-statement-table">
//                           <thead>
//                             <tr>
//                               <th>Month</th>
//                               <th>Year</th>
//                               <th>Monthly Electricity (₹)</th>
//                               <th>Payment Due (₹)</th>
//                               <th>Paid (₹)</th>
//                               <th>Security Adjusted (₹)</th>
//                               <th>Net Payable (₹)</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {(tenantStatement.length > 0 ? tenantStatement : [{}]).map((item, idx) => (
//                               <tr key={idx}>
//                                 <td>{item.MonthName || '-'}</td>
//                                 <td>{item.InvoiceYear || '-'}</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                                 <td>-</td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   )}

//                   {/* Other Expenses Row */}
//                   <div className="rp-table-row-custom" onClick={() => toggleSection('expense')}>
//                     <div className="rp-td rp-td-label">
//                       <span className="rp-icon rp-icon-expense"><IconDoc /></span>
//                       <span className="rp-item-name">Other Expenses</span>
//                       <span className={`rp-history-caret ${expandedSection === 'expense' ? 'rp-history-caret-open' : ''}`}>▾</span>
//                     </div>
//                     <div className="rp-td rp-td-amount" data-label="Total Due">{formatCurrency(expenseDue)}</div>
//                     <div className="rp-td rp-td-advance" data-label="Advance Used">₹0</div>
//                     <div className="rp-td rp-td-pay" data-label="Paid Now">
//                       <div className="rp-input-wrap">
//                         <span className="rp-input-prefix">₹</span>
//                         <input
//                           type="number"
//                           className="rp-input"
//                           placeholder="0"
//                           value={formData.ExpenseAmount}
//                           onChange={(e) => handleChange('ExpenseAmount', e.target.value)}
//                           step="0.01"
//                           min="0"
//                         />
//                       </div>
//                     </div>
//                     <div className="rp-td rp-td-received" data-label="Amount Received">{formatCurrency(parseFloat(formData.ExpenseAmount) || 0)}</div>
//                   </div>

//                   {/* Expenses History Panel */}
//                   {expandedSection === 'expense' && (
//                     <div className="rp-history-panel">
//                       {isLoadingExpenses ? (
//                         <div className="rv-statement-loading">
//                           <span className="rv-spinner-small"></span> Loading expenses...
//                         </div>
//                       ) : pendingExpenses.length > 0 ? (
//                         <div className="rv-pe-table-wrapper">
//                           <table className="rv-pe-table">
//                             <thead>
//                               <tr>
//                                 <th>Expense</th>
//                                 <th>Expense Date</th>
//                                 <th>Amount</th>
//                                 <th>Adjusted Amount</th>
//                                 <th>Paid Amount</th>
//                                 <th>Net Payable</th>
//                                 <th>Description</th>
//                                 <th>Status</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {pendingExpenses.map((expense) => (
//                                 <tr key={expense.ExpenseID}>
//                                   <td>{expense.LedgerName}</td>
//                                   <td>{new Date(expense.ExpenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
//                                   <td className="rv-pe-amount">{fmt(expense.Amount)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.AdvanceAdjusted || 0)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.PaidAmount || 0)}</td>
//                                   <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.NetPayable || 0)}</td>
//                                   <td>{expense.Description || '-'}</td>
//                                   <td>{expense.Status || '-'}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <div className="rv-pe-empty">No pending expenses found</div>
//                       )}
//                     </div>
//                   )}

//                   {/* Total Row */}
//                   <div className="rp-table-row-custom rp-table-total-custom">
//                     <div className="rp-td rp-td-label">
//                       <span className="rp-item-name rp-total-name">Total</span>
//                     </div>
//                     <div className="rp-td rp-td-amount rp-total-value" data-label="Total Due">{formatCurrency(totalDue)}</div>
//                     <div className="rp-td rp-td-advance rp-total-value" data-label="Advance Used">{formatCurrency(advanceUsed)}</div>
//                     <div className="rp-td rp-td-pay rp-total-value" data-label="Paid Now">{formatCurrency(totalPayable)}</div>
//                     <div className="rp-td rp-td-received rp-total-value" data-label="Amount Received">{formatCurrency(totalPayable)}</div>
//                   </div>
//                 </div>

//                 {/* ─── Payment Form Fields ────────────────────────── */}
//                 <div className="rp-extra-row">
//                   <div className="rp-field-group">
//                     <label className="rp-field-label">Payment Mode</label>
//                     <select
//                       className={`rp-select ${errors.PaymentMode ? 'rp-field-error' : ''}`}
//                       value={formData.PaymentMode}
//                       onChange={(e) => handleChange('PaymentMode', e.target.value)}
//                     >
//                       <option value="CASH">💵 Cash</option>
//                       <option value="UPI">📱 UPI</option>
//                       <option value="BANK">🏦 Bank Transfer</option>
//                       <option value="CHEQUE">📝 Cheque</option>
//                     </select>
//                     {errors.PaymentMode && <span className="rp-error-text">{errors.PaymentMode}</span>}
//                   </div>

//                   <div className="rp-field-group">
//                     <label className="rp-field-label">Reference No.</label>
//                     <input
//                       type="text"
//                       className="rp-text-input"
//                       placeholder="Enter reference number"
//                       value={formData.ReferenceNo}
//                       onChange={(e) => handleChange('ReferenceNo', e.target.value)}
//                     />
//                   </div>
//                 </div>

//                 <div className="rp-field-group rp-field-full">
//                   <label className="rp-field-label">Narration <span className="rp-optional">(Optional)</span></label>
//                   <input
//                     type="text"
//                     className="rp-text-input"
//                     placeholder="Enter narration"
//                     value={formData.Narration}
//                     onChange={(e) => handleChange('Narration', e.target.value)}
//                   />
//                 </div>

//                 {errors.general && <div className="rp-error-text rp-error-general">{errors.general}</div>}
//                 {message && <div className={`rp-message rp-message-${messageType}`}>{message}</div>}
//               </>
//             )}

//             {activeTab === 'security' && (
//               <SecurityDepositModal
//                 embedded
//                 depositData={securityDeposit}
//                 admissionId={bed?.admissionId}
//                 onClose={() => {}}
//                 onTransactionComplete={handleTransactionComplete}
//               />
//             )}

//             {activeTab === 'settlement' && (
//               <FullFinalSettlementSection
//                 admissionId={bed?.admissionId}
//                 onSettled={() => {
//                   if (onSuccess) onSuccess(bed.bedId, 'SETTLED');
//                 }}
//               />
//             )}
//           </AnimatedHeight>
//         </div>

//         {activeTab === 'overall' && (
//           <div className="rp-footer">
//             <button
//               type="button"
//               className="rp-btn rp-btn-secondary"
//               onClick={onClose}
//               disabled={isSubmitting}
//             >
//               Cancel
//             </button>
//             <button
//               type="button"
//               className="rp-btn rp-btn-primary"
//               onClick={handleSubmit}
//               disabled={isSubmitting || isFetchingReceipt}
//             >
//               {isSubmitting || isFetchingReceipt ? (
//                 <>
//                   <span className="rp-spinner"></span>
//                   Processing...
//                 </>
//               ) : (
//                 `Pay ${formatCurrency(totalPayable)}`
//               )}
//             </button>
//           </div>
//         )}
//       </div>

//       {showReceipt && receiptData && (
//         <div
//           className="rv-receipt-overlay"
//           onClick={() => {
//             setShowReceipt(false);
//             onClose();
//           }}
//         >
//           <div onClick={e => e.stopPropagation()}>
//             <RentReceipt
//               {...receiptData}
//               onClose={() => {
//                 setShowReceipt(false);
//                 onClose();
//               }}
//             />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

const OverallRentModal = ({
  pgId,
  bed,
  pgName,
  onClose,
  onSuccess,
  onSendReminder
}) => {
  const [activeTab, setActiveTab] = useState('overall') // 'overall' | 'security' | 'settlement'
  const [expandedSection, setExpandedSection] = useState(null) // 'rent' | 'electricity' | 'expense' | null

  const [formData, setFormData] = useState({
    RentAmount: '',
    ElectricityAmount: '',
    ExpenseAmount: '',
    PaymentMode: 'CASH',
    ReferenceNo: '',
    Narration: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [errors, setErrors] = useState({});

  const [overallDueData, setOverallDueData] = useState(null);
  const [isLoadingOverallDue, setIsLoadingOverallDue] = useState(false);

  const [tenantStatement, setTenantStatement] = useState([]);
  const [isLoadingStatement, setIsLoadingStatement] = useState(false);

  const [securityDeposit, setSecurityDeposit] = useState(null);
  const [isLoadingDeposit, setIsLoadingDeposit] = useState(false);

  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const [currentAdmissionId, setCurrentAdmissionId] = useState(null);

  const [isSendingReminder, setIsSendingReminder] = useState(false);

  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isFetchingReceipt, setIsFetchingReceipt] = useState(false);

  const statusColors = getStatusColor(bed?.rentStatus);
  const showReminder = ['DUE', 'OVERDUE', 'NOT PAID'].includes(bed?.rentStatus);

  // ─── Fetchers ────────────────────────────────────────────────
  const fetchOverallDue = async () => {
    if (!bed?.admissionId) return;
    setIsLoadingOverallDue(true);
    try {
      const response = await getOverallDue(bed.admissionId);
      console.log("response", response);
      if (response?.success && response?.data) {
        // Store the array data directly
        setOverallDueData(response.data);
      }
    } catch (error) {
      console.error('Error fetching overall due:', error);
    } finally {
      setIsLoadingOverallDue(false);
    }
  };

  const fetchTenantStatement = async () => {
    if (!bed?.admissionId || !pgId) return;
    setIsLoadingStatement(true);
    try {
      const response = await getTenantStatement(bed.admissionId, pgId);
      console.log("response",response);
      
      setTenantStatement(response || []);
    } catch (err) {
      console.error('Error fetching tenant statement:', err);
      setTenantStatement([]);
    } finally {
      setIsLoadingStatement(false);
    }
  };

  const fetchSecurityDeposit = async () => {
    if (!bed?.admissionId) return;
    setIsLoadingDeposit(true);
    try {
      const response = await getSecurityDeposit(bed.admissionId);
      setSecurityDeposit(response?.data || null);
    } catch (err) {
      console.error('Error fetching security deposit:', err);
      setSecurityDeposit(null);
    } finally {
      setIsLoadingDeposit(false);
    }
  };

  const fetchPendingExpenses = async () => {
    if (!bed?.admissionId || !pgId) return;
    setCurrentAdmissionId(bed.admissionId);
    setIsLoadingExpenses(true);
    try {
      const response = await getTenantPendingExpenses(pgId, bed.admissionId);
      if (response?.data && Array.isArray(response.data)) {
        setPendingExpenses(response.data);
      } else {
        setPendingExpenses([]);
      }
    } catch (err) {
      console.error('Error fetching pending expenses:', err);
      setPendingExpenses([]);
    } finally {
      setIsLoadingExpenses(false);
    }
  };

  const [tenantAdvance, setTenantAdvance] = useState({
    advanceDeposit: [],
    securityDeposit: []
  });
  const [isLoadingAdvance, setIsLoadingAdvance] = useState(false);

  const fetchTenantAdvance = async () => {
    if (!bed?.admissionId) return;
    setIsLoadingAdvance(true);
    try {
      const response = await getTenantAdvance(bed.admissionId);
      console.log("Advance response", response);

      if (response && response.advanceDeposit !== undefined) {
        setTenantAdvance({
          advanceDeposit: response.advanceDeposit || [],
          securityDeposit: response.securityDeposit || []
        });
      } else if (response?.success && response?.data) {
        setTenantAdvance({
          advanceDeposit: response.data.advanceDeposit || [],
          securityDeposit: response.data.securityDeposit || []
        });
      } else {
        setTenantAdvance({
          advanceDeposit: [],
          securityDeposit: []
        });
      }
    } catch (err) {
      console.error('Error fetching tenant advance:', err);
      setTenantAdvance({
        advanceDeposit: [],
        securityDeposit: []
      });
    } finally {
      setIsLoadingAdvance(false);
    }
  };

  useEffect(() => {
    if (bed?.admissionId) {
      fetchOverallDue();
      fetchSecurityDeposit();
      fetchTenantAdvance();
    }
  }, [bed?.admissionId]);

  useEffect(() => {
    if (bed?.admissionId && pgId) {
      fetchTenantStatement();
      fetchPendingExpenses();
    }
  }, [bed?.admissionId, pgId]);

  const handleTransactionComplete = (updatedDeposit) => {
    setSecurityDeposit(updatedDeposit);
    if (onSuccess) {
      onSuccess(bed.bedId, updatedDeposit?.Status);
    }
  };

  // ─── WhatsApp reminder ───────────────────────────────────────
  const sendWhatsAppReminder = async (bedData) => {
    if (!bedData) return;
    setIsSendingReminder(true);

    try {
      const customerName = bedData.name || 'Tenant';
      const dueDate = bedData.DueDate || new Date().toISOString();
      const billingMonth = new Date(dueDate).toLocaleString('en-US', { month: 'long', year: 'numeric' });
      const roomNumber = bedData.RoomNumber || bedData.roomNumber || 'N/A';
      const outstandingAmount = bedData.NetPayable || bedData.rent || 0;
      const pgNameValue = pgName || 'PG';
      const phoneNumbers = [`91${bedData.PhoneNumber}` || "916260216707"];

      const payload = {
        integrated_number: "918796203089",
        content_type: "template",
        payload: {
          messaging_product: "whatsapp",
          type: "template",
          template: {
            name: "aapkapg_overdue_final",
            language: { code: "en", policy: "deterministic" },
            namespace: "cbf3b2ba_dca4_40bd_9e8c_747d92ec2d3c",
            to_and_components: [
              {
                to: phoneNumbers,
                components: {
                  body_customer_name: { type: "text", value: customerName, parameter_name: "customer_name" },
                  body_billing_month: { type: "text", value: billingMonth, parameter_name: "billing_month" },
                  body_room_number: { type: "text", value: roomNumber.toString(), parameter_name: "room_number" },
                  body_outstanding_amount: { type: "text", value: outstandingAmount.toString(), parameter_name: "outstanding_amount" },
                  body_due_date: {
                    type: "text",
                    value: new Date(dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
                    parameter_name: "due_date"
                  },
                  body_pg_name: { type: "text", value: pgNameValue, parameter_name: "pg_name" }
                }
              }
            ]
          }
        }
      };

      const response = await sendWhatsAppOverdueReminder(payload);

      if (response?.success) {
        if (onSendReminder) onSendReminder(bedData);
        alert(`✅ WhatsApp reminder sent to ${customerName}!`);
      } else {
        alert(`❌ Failed to send reminder: ${response?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending WhatsApp reminder:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send WhatsApp reminder';
      alert(`❌ Error: ${errorMessage}`);
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleReminderClick = () => {
    if (bed) {
      sendWhatsAppReminder(bed);
    }
  };

  // ─── Overall payment submit ──────────────────────────────────
  const validate = () => {
    const newErrors = {};
    const { RentAmount, ElectricityAmount, ExpenseAmount, PaymentMode } = formData;

    const rent = parseFloat(RentAmount) || 0;
    const electricity = parseFloat(ElectricityAmount) || 0;
    const expense = parseFloat(ExpenseAmount) || 0;
    // const refNo = ReferenceNo || '';

    if (rent <= 0 && electricity <= 0 && expense <= 0) {
      newErrors.general = 'At least one amount must be greater than 0';
    }
    if (!PaymentMode) {
      newErrors.PaymentMode = 'Please select payment mode';
    }
    // if (!refNo||refNo==''||refNo==null) {
    //   newErrors.ReferenceNo = 'Please enter ref no.';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setMessage('');

    try {
      const userId = localStorage.getItem('userId') || 1;

      const payload = {
        AdmissionID: bed?.admissionId,
        RentAmount: parseFloat(formData.RentAmount) || 0,
        ElectricityAmount: parseFloat(formData.ElectricityAmount) || 0,
        ExpenseAmount: parseFloat(formData.ExpenseAmount) || 0,
        PaymentMode: formData.PaymentMode,
        DueinMonth: 0,
        ReferenceNo: formData.ReferenceNo?.trim() || '',
        UserId: parseInt(userId)
      };
      console.log("payload", payload);

      const response = await processOverallPayment(payload);
      console.log("response", response)
      if (response?.success) {
        setMessage('Payment processed successfully!');
        setMessageType('success');

        fetchOverallDue();
        fetchTenantStatement();
        fetchPendingExpenses();
        fetchTenantAdvance();

        if (onSuccess) {
          onSuccess(bed.bedId, '');
        }


        let receiptId = null;
        if (response.success && response.data && response.data.length > 0) {
          const lastArray = response.data[response.data.length - 1];
          if (lastArray && lastArray.length > 0 && lastArray[0].ReceiptId) {
            receiptId = lastArray[0].ReceiptId;
          }
        }

        console.log("Receipt ID:", receiptId);


        const receiptIdToFetch = receiptId || FIXED_RECEIPT_ID;

        try {
          setIsFetchingReceipt(true);
          const parsedReceipt = await fetchReceiptByNo(receiptIdToFetch);

          const advanceResponse = await getTenantAdvance(bed.admissionId);
          let advanceData = null;
          if (advanceResponse && advanceResponse.advanceDeposit !== undefined) {
            advanceData = advanceResponse;
          } else if (advanceResponse?.success && advanceResponse?.data) {
            advanceData = advanceResponse.data;
          }

          setReceiptData(mapReceiptToRentReceiptProps(parsedReceipt, advanceData));
          setShowReceipt(true);
        } catch (receiptErr) {
          console.error('Error fetching receipt:', receiptErr);
          setTimeout(() => onClose(), 1500);
        } finally {
          setIsFetchingReceipt(false);
        }
      } else {
        throw new Error(response?.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Error processing overall payment:', error);
      setMessage(error.message || 'Payment failed. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
  };

  // Get data from overallDueData array
  const getHeadData = (headName) => {
    if (!overallDueData || !Array.isArray(overallDueData)) return null;
    return overallDueData.find(item => item.Head === headName);
  };

  const rentData = getHeadData('RENT');
  const electricityData = getHeadData('ELECTRICITY');
  const expenseData = getHeadData('EXPENSE');
  const totalData = getHeadData('TOTAL');

  const rentDue = rentData?.TotalDue || 0;
  const electricityDue = electricityData?.TotalDue || 0;
  const expenseDue = expenseData?.TotalDue || 0;
  const totalDue = totalData?.TotalDue || 0;
  const advanceUsed = totalData?.AdvanceUsed || 0;
  const totalPayNow = totalData?.PayNow || 0;

  const totalPayable =
    (parseFloat(formData.RentAmount) || 0) +
    (parseFloat(formData.ElectricityAmount) || 0) +
    (parseFloat(formData.ExpenseAmount) || 0);

  const toggleSection = (section) => {
    setExpandedSection(prev => (prev === section ? null : section));
  };

  if (!bed) return null;

  return (
    <div className="rp-overlay" onClick={onClose}>
      <div className="rp-modal" onClick={e => e.stopPropagation()}>
        <div className="rp-header">
          <div>
            <h3 className="rp-title">{bed.name || 'Tenant'}</h3>
            <p className="rp-subtitle">
              <span className="rv-rp-badge" style={{ background: statusColors.bg, color: statusColors.color }}>
                <span className="rv-rp-dot" style={{ background: statusColors.dot }} />
                {bed.rentStatus === 'NOT PAID' ? 'NOT PAID' : (bed.rentStatus || 'NA')}
              </span>
            </p>
          </div>
          <div className="rp-header-actions">
            {showReminder && (
              <button
                className="rv-rp-reminder-btn"
                onClick={handleReminderClick}
                disabled={isSendingReminder}
                title="Send WhatsApp reminder"
              >
                {isSendingReminder ? (
                  <>
                    <span className="rv-spinner-small"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 01-3.46 0" />
                    </svg>
                    Send Reminder
                  </>
                )}
              </button>
            )}
            <button className="rp-close" onClick={onClose} aria-label="Close">
              <IconClose />
            </button>
          </div>
        </div>

        {/* ─── Tabs ─────────────────────────────────────────── */}
        <div className="rp-tabs">
          <button
            className={`rp-tab ${activeTab === 'overall' ? 'rp-tab-active' : ''}`}
            onClick={() => setActiveTab('overall')}
          >
            Regular Payment
          </button>
          <button
            className={`rp-tab ${activeTab === 'security' ? 'rp-tab-active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security Deposit
          </button>
          <button
            className={`rp-tab ${activeTab === 'settlement' ? 'rp-tab-active' : ''}`}
            onClick={() => setActiveTab('settlement')}
          >
            Full and Final Payment
          </button>
        </div>

        <div className="rp-body">
          <AnimatedHeight>
            {activeTab === 'overall' && (
              <>
                {/* ─── Table Design ─────────────────────────────────── */}
                <div className="rp-table-wrapper">
                  {/* Table Header */}
                  <div className="rp-table-header">
                    <div className="rp-th">Head</div>
                    <div className="rp-th">Total Due</div>
                    <div className="rp-th">Advance Used</div>
                    <div className="rp-th">Paid Now</div>
                    <div className="rp-th">Amount Received</div>
                  </div>

                  {/* Rent Row */}
                  <div className="rp-table-row-custom">
                    <div className="rp-td rp-td-label">
                      <span className="rp-icon rp-icon-rent"><IconHouse /></span>
                      <span className="rp-item-name">Rent</span>
                      <span
                        className={`rp-history-caret ${expandedSection === 'rent' ? 'rp-history-caret-open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection('rent');
                        }}
                      >▾</span>
                    </div>
                    <div className="rp-td rp-td-amount" data-label="Total Due">{formatCurrency(rentData?.TotalDue || 0)}</div>
                    <div className="rp-td rp-td-advance" data-label="Advance Used">{formatCurrency(rentData?.AdvanceUsed || 0)}</div>
                    <div className="rp-td rp-td-pay" data-label="Paid Now">{formatCurrency(rentData?.PayNow || 0)}</div>
                    <div className="rp-td rp-td-received" data-label="Amount Received">
                      <div className="rp-input-wrap">
                        <span className="rp-input-prefix">₹</span>
                        <input
                          type="number"
                          className="rp-input"
                          placeholder="0"
                          value={formData.RentAmount}
                          onChange={(e) => handleChange('RentAmount', e.target.value)}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Rent History Panel */}
                  {expandedSection === 'rent' && (
                    <div className="rp-history-panel">
                      {isLoadingStatement ? (
                        <div className="rv-statement-loading">
                          <span className="rv-spinner-small"></span> Loading rent history...
                        </div>
                      ) : tenantStatement.length > 0 ? (
                        <div className="rv-statement-table-wrapper">
                          <table className="rv-statement-table">
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th>Year</th>
                                <th>Monthly Rent (₹)</th>
                                <th>Rent Due (₹)</th>
                                <th>Paid (₹)</th>
                                <th>Security Adjusted (₹)</th>
                                <th>Net Payable (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tenantStatement.map((item, idx) => (
                                <tr key={idx}>
                                  <td>{item.MonthName}</td>
                                  <td>{item.InvoiceYear}</td>
                                  <td>{(item.RentAmount || 0).toLocaleString('en-IN')}</td>
                                  <td>{(item.TotalAmount || 0).toLocaleString('en-IN')}</td>
                                  <td className="rv-paid-amount">{(item.PaidAmount || 0).toLocaleString('en-IN')}</td>
                                  <td className="rv-adjusted-amount">{(item.AdvanceAdjusted || 0).toLocaleString('en-IN')}</td>
                                  <td className="rv-netpayable-amount">{(item.NetPayable || 0).toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="rv-pe-empty">No rent history found</div>
                      )}
                    </div>
                  )}

                  {/* Electricity Row */}
                  <div className="rp-table-row-custom">
                    <div className="rp-td rp-td-label">
                      <span className="rp-icon rp-icon-electricity"><IconBolt /></span>
                      <span className="rp-item-name">Electricity</span>
                      <span
                        className={`rp-history-caret ${expandedSection === 'electricity' ? 'rp-history-caret-open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection('electricity');
                        }}
                      >▾</span>
                    </div>
                    <div className="rp-td rp-td-amount" data-label="Total Due">{formatCurrency(electricityData?.TotalDue || 0)}</div>
                    <div className="rp-td rp-td-advance" data-label="Advance Used">{formatCurrency(electricityData?.AdvanceUsed || 0)}</div>
                    <div className="rp-td rp-td-pay" data-label="Paid Now">{formatCurrency(electricityData?.PayNow || 0)}</div>
                    <div className="rp-td rp-td-received" data-label="Amount Received">
                      <div className="rp-input-wrap">
                        <span className="rp-input-prefix">₹</span>
                        <input
                          type="number"
                          className="rp-input"
                          placeholder="0"
                          value={formData.ElectricityAmount}
                          onChange={(e) => handleChange('ElectricityAmount', e.target.value)}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Electricity History Panel */}
                  {expandedSection === 'electricity' && (
                    <div className="rp-history-panel">
                      <div className="rv-statement-table-wrapper">
                        <table className="rv-statement-table">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Year</th>
                              <th>Monthly Electricity (₹)</th>
                              <th>Payment Due (₹)</th>
                              <th>Paid (₹)</th>
                              <th>Security Adjusted (₹)</th>
                              <th>Net Payable (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(tenantStatement.length > 0 ? tenantStatement : [{}]).map((item, idx) => (
                              <tr key={idx}>
                                <td>{item.MonthName || '-'}</td>
                                <td>{item.InvoiceYear || '-'}</td>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Other Expenses Row */}
                  <div className="rp-table-row-custom">
                    <div className="rp-td rp-td-label">
                      <span className="rp-icon rp-icon-expense"><IconDoc /></span>
                      <span className="rp-item-name">Other Expenses</span>
                      <span
                        className={`rp-history-caret ${expandedSection === 'expense' ? 'rp-history-caret-open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection('expense');
                        }}
                      >▾</span>
                    </div>
                    <div className="rp-td rp-td-amount" data-label="Total Due">{formatCurrency(expenseData?.TotalDue || 0)}</div>
                    <div className="rp-td rp-td-advance" data-label="Advance Used">{formatCurrency(expenseData?.AdvanceUsed || 0)}</div>
                    <div className="rp-td rp-td-pay" data-label="Paid Now">{formatCurrency(expenseData?.PayNow || 0)}</div>
                    <div className="rp-td rp-td-received" data-label="Amount Received">
                      <div className="rp-input-wrap">
                        <span className="rp-input-prefix">₹</span>
                        <input
                          type="number"
                          className="rp-input"
                          placeholder="0"
                          value={formData.ExpenseAmount}
                          onChange={(e) => handleChange('ExpenseAmount', e.target.value)}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expenses History Panel */}
                  {expandedSection === 'expense' && (
                    <div className="rp-history-panel">
                      {isLoadingExpenses ? (
                        <div className="rv-statement-loading">
                          <span className="rv-spinner-small"></span> Loading expenses...
                        </div>
                      ) : pendingExpenses.length > 0 ? (
                        <div className="rv-pe-table-wrapper">
                          <table className="rv-pe-table">
                            <thead>
                              <tr>
                                <th>Expense</th>
                                <th>Expense Date</th>
                                <th>Amount</th>
                                <th>Adjusted Amount</th>
                                <th>Paid Amount</th>
                                <th>Net Payable</th>
                                <th>Description</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pendingExpenses.map((expense) => (
                                <tr key={expense.ExpenseID}>
                                  <td>{expense.LedgerName}</td>
                                  <td>{new Date(expense.ExpenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                  <td className="rv-pe-amount">{fmt(expense.Amount)}</td>
                                  <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.AdvanceAdjusted || 0)}</td>
                                  <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.PaidAmount || 0)}</td>
                                  <td className="rv-pe-amount" style={{ fontWeight: '700' }}>{fmt(expense.NetPayable || 0)}</td>
                                  <td>{expense.Description || '-'}</td>
                                  <td>{expense.Status || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="rv-pe-empty">No pending expenses found</div>
                      )}
                    </div>
                  )}

                  {/* Total Row */}
                  <div className="rp-table-row-custom rp-table-total-custom">
                    <div className="rp-td rp-td-label">
                      <span className="rp-item-name rp-total-name">Total</span>
                    </div>
                    <div className="rp-td rp-td-amount rp-total-value" data-label="Total Due">{formatCurrency(totalDue)}</div>
                    <div className="rp-td rp-td-advance rp-total-value" data-label="Advance Used">{formatCurrency(advanceUsed)}</div>
                    <div className="rp-td rp-td-pay rp-total-value" data-label="Paid Now">{formatCurrency(totalPayNow)}</div>
                    <div className="rp-td rp-td-received rp-total-value" data-label="Amount Received">{formatCurrency(totalPayable)}</div>
                  </div>
                </div>

                {/* ─── Payment Form Fields ────────────────────────── */}
                <div className="rp-extra-row">
                  <div className="rp-field-group">
                    <label className="rp-field-label">Payment Mode</label>
                    <select
                      className={`rp-select ${errors.PaymentMode ? 'rp-field-error' : ''}`}
                      value={formData.PaymentMode}
                      onChange={(e) => handleChange('PaymentMode', e.target.value)}
                    >
                      <option value="CASH">💵 Cash</option>
                      <option value="UPI">📱 UPI</option>
                      <option value="BANK">🏦 Bank Transfer</option>
                      <option value="CHEQUE">📝 Cheque</option>
                    </select>
                    {errors.PaymentMode && <span className="rp-error-text">{errors.PaymentMode}</span>}
                  </div>

                  <div className="rp-field-group">
                    <label className="rp-field-label">Reference No.</label>
                    <input
                      type="text"
                      className="rp-text-input"
                      placeholder="Enter reference number"
                      value={formData.ReferenceNo}
                      onChange={(e) => handleChange('ReferenceNo', e.target.value)}
                    />
                    {/* {errors.ReferenceNo && <span className="rp-error-text">{errors.ReferenceNo}</span>} */}
                  </div>
                </div>

                <div className="rp-field-group rp-field-full">
                  <label className="rp-field-label">Narration <span className="rp-optional">(Optional)</span></label>
                  <input
                    type="text"
                    className="rp-text-input"
                    placeholder="Enter narration"
                    value={formData.Narration}
                    onChange={(e) => handleChange('Narration', e.target.value)}
                  />
                </div>

                {errors.general && <div className="rp-error-text rp-error-general">{errors.general}</div>}
                {message && <div className={`rp-message rp-message-${messageType}`}>{message}</div>}
              </>
            )}

            {activeTab === 'security' && (
              <SecurityDepositModal
                embedded
                depositData={securityDeposit}
                admissionId={bed?.admissionId}
                onClose={() => { }}
                onTransactionComplete={handleTransactionComplete}
              />
            )}

            {activeTab === 'settlement' && (
              <FullFinalSettlementSection
                admissionId={bed?.admissionId}
                onSettled={() => {
                  if (onSuccess) onSuccess(bed.bedId, 'SETTLED');
                }}
              />
            )}
          </AnimatedHeight>
        </div>

        {activeTab === 'overall' && (
          <div className="rp-footer">
            <button
              type="button"
              className="rp-btn rp-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rp-btn rp-btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || isFetchingReceipt}
            >
              {isSubmitting || isFetchingReceipt ? (
                <>
                  <span className="rp-spinner"></span>
                  Processing...
                </>
              ) : (
                `Pay ${formatCurrency(totalPayable)}`
              )}
            </button>
          </div>
        )}
      </div>

      {showReceipt && receiptData && (
        <div
          className="rv-receipt-overlay"
          onClick={() => {
            setShowReceipt(false);
            onClose();
          }}
        >
          <div onClick={e => e.stopPropagation()}>
            <RentReceipt
              {...receiptData}
              onClose={() => {
                setShowReceipt(false);
                onClose();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const OwnerRevenue = ({ selectedPG, selectedPGId }) => {
  const [floor, setFloor] = useState('all')
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [search, setSearch] = useState('')
  const [rooms, setRooms] = useState([])
  const [totals, setTotals] = useState({ total: 0, collected: 0, due: 0, overdue: 0, notPaid: 0 })
  const [floors, setFloors] = useState(['all'])
  const [localRentStatus, setLocalRentStatus] = useState({})
  const [selectedBed, setSelectedBed] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() + 5);
    now.setMinutes(now.getMinutes() + 30);
    return now.toISOString().slice(0, 16);
  });
  const [isGenerating, setIsGenerating] = useState(false)
  const [generateMessage, setGenerateMessage] = useState(null)
  const [isGeneratingElectricity, setIsGeneratingElectricity] = useState(false)
  const [electricityMessage, setElectricityMessage] = useState(null)

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })

  const handleSendReminder = async (bed) => {
    console.log('Sending reminder for:', bed.name)
    setToast({
      show: true,
      message: `WhatsApp reminder sent to ${bed.name || 'tenant'}!`,
      type: 'success'
    })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const floorOrder = {
    'Ground Floor': 0,
    '1st Floor': 1,
    '2nd Floor': 2,
    '3rd Floor': 3,
    '4th Floor': 4,
    '5th Floor': 5
  }

  const fetchRevenueData = async () => {
    try {
      const response = await getPGRevenueStatus(selectedPGId, selectedDate)
      const roomsMap = new Map()
      response.occupancyData.forEach(item => {
        const roomId = item.RoomID
        const floorStr = item.Floor || 'Unknown'
        if (!roomsMap.has(roomId)) {
          roomsMap.set(roomId, {
            id: item.RoomNumber,
            floor: floorStr,
            type: item.RoomType?.toLowerCase() || 'unknown',
            beds: []
          })
        }
        const room = roomsMap.get(roomId)
        const statusValue = item.status || item.Status || 'VACANT'
        const occupancyStatus = item.OccupancyStatus === 'VACANT' ? 'VACANT' : statusValue
        room.beds.push({
          bedId: item.BedID,
          name: item.TenantName || '',
          status: occupancyStatus || 'VACANT',
          rent: item.NetPayable || 0,
          rentStatus: statusValue || 'NA',
          admissionId: item.AdmissionID || item.admissionId,
          RentAmount: item.RentAmount,
          ElectricityAmount: item.ElectricityAmount,
          TotalAmount: item.TotalAmount,
          NetPayable: item.NetPayable,
          RentdueInMonth: item.RentDueInMonth || item.RentdueInMonth,
          DueDate: item.DueDate,
          RoomNumber: item.RoomNumber,
          PhoneNumber: item.PhoneNumber
        })
      })

      const roomsData = Array.from(roomsMap.values()).sort((a, b) => {
        const aFloorOrder = floorOrder[a.floor] !== undefined ? floorOrder[a.floor] : 999
        const bFloorOrder = floorOrder[b.floor] !== undefined ? floorOrder[b.floor] : 999
        if (aFloorOrder !== bFloorOrder) return aFloorOrder - bFloorOrder
        const aNum = parseInt(a.id), bNum = parseInt(b.id)
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
        return String(a.id).localeCompare(String(b.id))
      })
      setRooms(roomsData)

      const uniqueFloorsSet = new Set(roomsData.map(r => r.floor))
      const uniqueFloors = ['all', ...Array.from(uniqueFloorsSet).sort((a, b) => {
        const aOrder = floorOrder[a] !== undefined ? floorOrder[a] : 999
        const bOrder = floorOrder[b] !== undefined ? floorOrder[b] : 999
        return aOrder - bOrder
      })]
      setFloors(uniqueFloors)

      if (response.totalsArray && response.totalsArray[0]) {
        setTotals({
          total: response.totalsArray[0].Total || 0,
          collected: response.totalsArray[0].Collected || 0,
          due: response.totalsArray[0].Due || 0,
          overdue: response.totalsArray[0].Overdue || 0,
          notPaid: response.totalsArray[0].NotPaid || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error)
    }
  }

  useEffect(() => {
    if (selectedPGId) fetchRevenueData()
  }, [selectedPGId, selectedDate])

  const formatSQLDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const formatDisplayDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  const handleAutoGenerate = async () => {
    if (!selectedPGId) {
      setGenerateMessage({ type: 'error', text: 'No PG selected' })
      setTimeout(() => setGenerateMessage(null), 3000)
      return
    }
    setIsGenerating(true)
    setGenerateMessage(null)
    try {
      const formattedDateTime = formatSQLDateTime(selectedDate);
      const data = { PGID: selectedPGId, CurrDate: formattedDateTime }
      console.log('Auto-generate rent with data:', data)
      const response = await autoGenerateRent(data)
      if (response.success) {
        setGenerateMessage({ type: 'success', text: `Rent generated successfully! ${response.data?.affectedRecords || 0} records affected.` })
        await fetchRevenueData()
      } else {
        setGenerateMessage({ type: 'error', text: response.message || 'Failed to generate rent' })
      }
    } catch (error) {
      console.error('Error auto-generating rent:', error)
      setGenerateMessage({ type: 'error', text: error.message || 'An error occurred' })
    } finally {
      setIsGenerating(false)
      setTimeout(() => setGenerateMessage(null), 5000)
    }
  }

  const handleAutoGenerateElectricity = async () => {
    if (!selectedPGId) {
      setElectricityMessage({ type: 'error', text: 'No PG selected' })
      setTimeout(() => setElectricityMessage(null), 3000)
      return
    }
    const date = new Date(selectedDate)
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    setIsGeneratingElectricity(true)
    setElectricityMessage(null)
    try {
      const data = { PGID: selectedPGId, Month: month, Year: year }
      console.log('Auto-generate electricity bill with data:', data)
      const response = await autoGenerateElectricityBill(data)
      if (response.success) {
        setElectricityMessage({ type: 'success', text: `Electricity bill generated successfully! ${response.data?.affectedRecords || 0} records affected.` })
        await fetchRevenueData()
      } else {
        setElectricityMessage({ type: 'error', text: response.message || 'Failed to generate electricity bill' })
      }
    } catch (error) {
      console.error('Error auto-generating electricity bill:', error)
      setElectricityMessage({ type: 'error', text: error.message || 'An error occurred' })
    } finally {
      setIsGeneratingElectricity(false)
      setTimeout(() => setElectricityMessage(null), 5000)
    }
  }

  const handleMarkRent = async (bedId, newStatus) => {
    setLocalRentStatus(prev => ({ ...prev, [bedId]: newStatus }))
    await fetchRevenueData()
  }

  const toggleStatusFilter = (statusKey) => {
    setSelectedStatuses(prev =>
      prev.includes(statusKey) ? prev.filter(s => s !== statusKey) : [...prev, statusKey]
    )
  }

  const filtered = rooms
    .map(room => {
      const filteredBeds = room.beds.filter(bed => {
        const matchesSearch = !search ||
          bed.name?.toLowerCase().includes(search.toLowerCase()) ||
          `room ${room.id}`.toLowerCase().includes(search.toLowerCase())
        let matchesStatus = true
        if (selectedStatuses.length > 0) {
          const bedStatus = localRentStatus[bed.bedId] || bed.rentStatus
          matchesStatus = selectedStatuses.includes(bedStatus)
        }
        return matchesSearch && matchesStatus
      })
      const matchesFloor = floor === 'all' || room.floor === floor
      if (matchesFloor && filteredBeds.length > 0) {
        return { ...room, beds: filteredBeds }
      }
      return null
    })
    .filter(room => room !== null)

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  return (
    <div className="rv-root">
      {toast.show && (
        <div className={`rv-toast rv-toast-${toast.type}`}>
          <span className="rv-toast-icon">✓</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Commented out auto-generate section as per original */}

      {generateMessage && (
        <div className={`rv-message rv-message-${generateMessage.type}`}>
          {generateMessage.text}
        </div>
      )}
      {electricityMessage && (
        <div className={`rv-message rv-message-${electricityMessage.type}`}>
          {electricityMessage.text}
        </div>
      )}

      <div className="rv-summary">
        <div className="rv-legend">
          {['PAID', 'DUE', 'OVERDUE', 'NOT PAID'].map(k => (
            <span key={k} className="rv-legend-item">
              <span className="rv-dot" style={{ background: STATUS_CFG[k].dot }} />
              {STATUS_CFG[k].label}
            </span>
          ))}
        </div>
        <div className="rv-stats">
          <span>Total: <strong>{fmt(totals.total)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Collected: <strong className="rv-green-1">{fmt(totals.collected)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Due: <strong className="rv-blue-1">{fmt(totals.due)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Overdue: <strong className="rv-orange-1">{fmt(totals.overdue)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Not Paid: <strong className="rv-red-1">{fmt(totals.notPaid)}</strong></span>
        </div>
      </div>

      <div className="rv-controls">
        <div className="rv-search-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input className="rv-search" placeholder="Search room or tenant…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="rv-filter-group">
          {floors.map(f => (
            <button key={f} className={`rv-floor-pill ${floor === f ? 'active' : ''}`} onClick={() => setFloor(f)}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
        <div className="rv-filter-group">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`rv-status-pill`}
              style={{
                background: selectedStatuses.includes(f.key) ? f.bg : '#F3F4F6',
                color: selectedStatuses.includes(f.key) ? 'white' : '#4B5563',
                borderColor: selectedStatuses.includes(f.key) ? f.bg : '#E5E7EB',
                transition: 'all 0.2s ease'
              }}
              onClick={() => toggleStatusFilter(f.key)}
            >
              {f.label}
              {selectedStatuses.includes(f.key) && <span className="rv-checkmark"> ✓</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="rv-grid">
        {filtered.map(room => (
          <div key={room.id} className="rv-card">
            <div className="rv-card-head">
              <div className="rv-card-head-left">
                <span className="rv-card-room">Room {room.id}</span>
                <div className="rv-card-badges">
                  <span className="rv-card-floor">{room.floor}</span>
                  <span className="rv-card-type">{room.type}</span>
                </div>
              </div>
            </div>

            <div className="rv-pill-grid">
              {room.beds.map((bed, i) => {
                const statusCfg = STATUS_CFG[bed.status] || STATUS_CFG.VACANT
                const effectiveRentStatus = localRentStatus[bed.bedId] || bed.rentStatus
                // const isClickable = effectiveRentStatus !== 'PAID' && bed.status !== 'VACANT'
                const isClickable = bed.status !== 'VACANT'

                return (
                  <div key={i} className="rv-pill-wrap">
                    <div
                      className={`rv-pill ${isClickable ? 'rv-pill-clickable' : ''}`}
                      style={{ background: statusCfg.bg, borderColor: statusCfg.border }}
                      onClick={() => {
                        if (isClickable) {
                          setSelectedBed(bed)
                          setIsModalOpen(true)
                        }
                      }}
                    >
                      <span className="rv-dot" style={{ background: statusCfg.dot }} />
                      <span className="rv-pill-name" style={{ color: statusCfg.text }}>
                        {bed.name || statusCfg.label}
                      </span>
                    </div>
                  </div>
                )
              })}
              {room.beds.length < 4 && [...Array(4 - room.beds.length)].map((_, i) => (
                <div key={`empty-${i}`} className="rv-pill rv-pill-ghost" />
              ))}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="rv-no-results">No tenants match your search criteria</div>
      )}

      {isModalOpen && (
        <OverallRentModal
          pgId={selectedPGId}
          bed={selectedBed}
          pgName={selectedPG}
          onClose={() => { setIsModalOpen(false); setSelectedBed(null); }}
          onSuccess={handleMarkRent}
          onSendReminder={handleSendReminder}
        />
      )}
    </div>
  )
}

export default OwnerRevenue